<template>
  <div class="egresos-container">
    <!-- Tabla de Órdenes de Egreso -->
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center flex-wrap">
        <span class="egresos-title">Gestión de Egresos</span>
        <div class="d-flex gap-2">
          <v-btn
            color="success"
            prepend-icon="mdi-file-excel"
            @click="exportEgresosToCSV"
            size="default"
            variant="outlined"
            :loading="exportingCSV"
          >
            <span class="d-none d-sm-inline">Exportar</span>
            <span class="d-sm-none">CSV</span>
          </v-btn>
          <v-btn
            color="primary"
            prepend-icon="mdi-plus"
            @click="showNewEgresoModal"
            size="default"
          >
            <span class="d-none d-sm-inline">Nueva Salida</span>
            <span class="d-sm-none">Nueva</span>
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <!-- Filtros -->
        <v-row class="mb-4 egresos-filters">
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.dateFrom"
              label="Fecha desde"
              type="date"
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.dateTo"
              label="Fecha hasta"
              type="date"
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.search"
              prepend-inner-icon="mdi-magnify"
              label="Buscar"
              placeholder="Orden, cliente, RUT..."
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Tabla -->
        <ResponsiveDataTable
          :items="filteredEgresos"
          :headers="[
            { title: 'Número de Orden', key: 'order_number', align: 'start', sortable: true },
            { title: 'Fecha', key: 'created_at', align: 'start', sortable: true },
            { title: 'Cliente', key: 'customer.name', align: 'start', sortable: true },
            { title: 'RUT', key: 'customer.tax_id', align: 'start', sortable: true },
            { title: 'Estrategia', key: 'strategy', align: 'center', sortable: true },
            { title: 'SKUs', key: 'items_count', align: 'end', sortable: true },
            { title: 'Acciones', key: 'actions', align: 'center', sortable: false }
          ]"
          :loading="loading"
          :search="filters.search"
          class="elevation-1 egresos-table"
          :show-select="false"
          items-per-page="10"
          density="default"
          hover
        >
          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:item.customer.name="{ item }">
            {{ item.customer?.name || 'No especificado' }}
          </template>

          <template v-slot:item.customer.tax_id="{ item }">
            {{ item.customer?.tax_id || '-' }}
          </template>

          <template v-slot:item.strategy="{ item }">
            <v-chip :color="getStrategyColor(item.strategy)" size="small">
              {{ getStrategyText(item.strategy) }}
            </v-chip>
          </template>

          <template v-slot:item.items_count="{ item }">
            {{ item.items_count || 0 }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon="mdi-eye"
              size="small"
              @click="viewEgreso(item)"
              color="primary"
              variant="text"
              class="mr-1"
            ></v-btn>
            <v-btn
              icon="mdi-delete"
              size="small"
              @click="deleteEgreso(item)"
              color="error"
              variant="text"
            ></v-btn>
          </template>
        </ResponsiveDataTable>
      </v-card-text>
    </v-card>

    <!-- Modal Nueva Salida -->
    <v-dialog v-model="showCreateModal" max-width="1200px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ isEditingEgreso ? 'Editar Salida' : 'Nueva Salida' }}</span>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="egresoForm" v-model="formValid">
            <!-- Cliente y Estrategia -->
            <v-row>
              <v-col cols="12" md="8">
                <ClienteSelector
                  v-model="newEgreso.customer_id"
                  @customer-selected="onCustomerSelected"
                  @customer-created="onCustomerCreated"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="newEgreso.strategy"
                  :items="strategyOptions"
                  label="Estrategia de Picking"
                  variant="outlined"
                  density="compact"
                  @update:model-value="onStrategyChanged"
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <!-- Ubicación de Origen (OBLIGATORIO) -->
            <v-row>
              <v-col cols="12">
                <v-alert
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="mb-0"
                >
                  <strong>Bodega de Origen:</strong> Seleccione la bodega desde donde se realizará la salida de productos.
                </v-alert>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="newEgreso.source_warehouse_id"
                  :items="warehousesForEgreso"
                  item-title="name"
                  item-value="id"
                  label="Bodega de Origen *"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || 'Bodega es requerida']"
                  @update:model-value="onWarehouseChanged"
                  prepend-inner-icon="mdi-warehouse"
                  required
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.raw.code }} - {{ item.raw.address || 'Sin dirección' }}</v-list-item-subtitle>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <!-- Información adicional -->
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="newEgreso.notes"
                  label="Notas adicionales"
                  rows="2"
                  variant="outlined"
                  density="compact"
                ></v-textarea>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- Alerta si no se ha seleccionado ubicación -->
            <v-alert
              v-if="!isLocationSelected"
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-4"
            >
              <v-icon start>mdi-alert</v-icon>
              Debe seleccionar una <strong>Bodega</strong> de origen antes de agregar productos.
            </v-alert>

            <!-- Productos -->
            <div class="d-flex justify-space-between align-center mb-4">
              <h3>Productos</h3>
              <div class="d-flex gap-2">
                <v-btn
                  color="secondary"
                  prepend-icon="mdi-barcode"
                  @click="startSingleScan"
                  size="small"
                  variant="outlined"
                  :disabled="loadingInitialData || !isLocationSelected"
                >
                  Escanear Productos
                </v-btn>
                <!--
                <v-btn
                  color="info"
                  prepend-icon="mdi-barcode-scan"
                  @click="startMultipleScan"
                  size="small"
                  variant="outlined"
                  :disabled="loadingInitialData"
                >
                  Escaneo Masivo
                </v-btn>
                <v-btn
                  color="primary"
                  prepend-icon="mdi-plus"
                  @click="addProductManually"
                  size="small"
                  :disabled="loadingInitialData"
                >
                  Agregar Manual
                </v-btn>
                -->
              </div>
            </div>

            <!-- Tabla de productos -->
            <v-card variant="outlined" class="mb-4" v-if="newEgreso.items.length > 0">
              <v-card-text class="pa-2">
                <v-table class="egresos-items-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="product-col">Producto</th>
                        <th class="sku-col">SKU</th>
                        <th class="quantity-col">Cantidad</th>
                        <th class="lot-col">Lote</th>
                        <th class="expiry-col">Vencimiento</th>
                        <th class="serial-col">S/N</th>
                        <th class="production-col">Fecha Fabricación</th>
                        <th class="strategy-col">Estrategia</th>
                        <th class="actions-col">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in newEgreso.items" :key="index">
                        <td class="product-col">{{ item.product_name || 'Producto no encontrado' }}</td>
                        <td class="sku-col">{{ item.product_sku || '-' }}</td>
                        <td class="quantity-col">{{ item.quantity }}</td>
                        <td class="lot-col">{{ item.lot_number || '-' }}</td>
                        <td class="expiry-col">{{ formatDate(item.expiry_date) || '-' }}</td>
                        <td class="serial-col">{{ item.serial_number || '-' }}</td>
                        <td class="production-col">{{ formatDate(item.production_date) || '-' }}</td>
                        <td class="strategy-col">
                          <v-chip 
                            v-if="!item.strategy_compliant"
                            color="warning"
                            size="small"
                            prepend-icon="mdi-alert"
                            @click="showStrategyWarning(item)"
                          >
                            No cumple
                          </v-chip>
                          <v-chip 
                            v-else
                            color="success"
                            size="small"
                            prepend-icon="mdi-check"
                          >
                            Cumple
                          </v-chip>
                        </td>
                        <td class="actions-col">
                          <v-btn
                            icon="mdi-delete"
                            size="small"
                            @click="removeProduct(index)"
                            color="error"
                            variant="text"
                          ></v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-table>
              </v-card-text>
            </v-card>

            <!-- Mensaje cuando no hay productos -->
            <v-alert
              v-if="newEgreso.items.length === 0"
              type="info"
              variant="tonal"
              class="mb-4"
            >
              No hay productos agregados. Use los botones de escaneo o agregue productos manualmente.
            </v-alert>

            <!-- Panel de escaneo -->
            <v-card 
              v-if="showBarcodeScanner || isMultipleScanMode"
              variant="outlined"
              class="mb-4"
            >
              <v-card-title class="text-h6">
                {{ isMultipleScanMode ? 'Escaneo Masivo' : 'Escaneo de Productos' }}
              </v-card-title>
              <v-card-text>
                <v-textarea
                  ref="scannerInput"
                  v-model="multipleScanInput"
                  :label="isMultipleScanMode ? 'Pegue códigos aquí (uno por línea)' : 'Escanee o ingrese código'"
                  :rows="isMultipleScanMode ? 8 : 2"
                  variant="outlined"
                  density="compact"
                  @keyup.enter="isMultipleScanMode ? null : processScannedBarcode(multipleScanInput)"
                  :hint="isMultipleScanMode ? 'Pegue múltiples códigos, uno por línea' : 'Presione Enter para procesar'"
                  persistent-hint
                ></v-textarea>
                
                <div class="d-flex gap-2 mt-3">
                  <v-btn
                    color="primary"
                    @click="isMultipleScanMode ? processMultipleCodes() : processScannedBarcode(multipleScanInput)"
                    :disabled="!multipleScanInput.trim()"
                  >
                    {{ isMultipleScanMode ? 'Procesar Códigos' : 'Procesar Código' }}
                  </v-btn>
                  <v-btn
                    color="grey"
                    @click="closeBarcodeScanner"
                    variant="outlined"
                  >
                    Cancelar
                  </v-btn>
                  <v-btn
                    v-if="isMultipleScanMode"
                    color="info"
                    @click="clearMultipleScanInput"
                    variant="outlined"
                  >
                    Limpiar
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <!-- Loading inicial -->
            <v-card variant="outlined" v-if="loadingInitialData" class="mb-4">
              <v-card-text class="text-center">
                <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
                <div>Cargando datos del sistema...</div>
              </v-card-text>
            </v-card>

            <!-- Resumen -->
            <v-card variant="outlined" v-if="newEgreso.items.length > 0">
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="8">
                    <div class="text-h6">Resumen</div>
                  </v-col>
                  <v-col cols="12" md="4" class="text-right">
                    <div>
                      <strong>Total de SKUs:</strong> {{ newEgreso.items.length }}
                    </div>
                    <div>
                      <strong>Total de Unidades:</strong> {{ totalQuantity }}
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeCreateModal">
            Cancelar
          </v-btn>
          <v-btn 
            color="primary"
            @click="saveEgreso"
            :loading="submitting"
            :disabled="!canSaveEgreso"
          >
            {{ isEditingEgreso ? 'Actualizar' : 'Crear Salida' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de advertencia de estrategia -->
    <v-dialog v-model="showStrategyWarningDialog" max-width="600px">
      <v-card>
        <v-card-title class="text-h5">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          Advertencia de Estrategia
        </v-card-title>
        
        <v-card-text>
          <v-alert type="warning" class="mb-3">
            {{ strategyWarningMessage }}
          </v-alert>
          
          <div v-if="suggestedProduct" class="mt-3">
            <h4>Producto Sugerido:</h4>
            <v-card variant="outlined" class="mt-2">
              <v-card-text>
                <div><strong>{{ suggestedProduct.type === 'lot' ? 'Lote' : 'Fecha de Vencimiento' }}:</strong> {{ suggestedProduct.value }}</div>
                <div><strong>Cantidad Disponible:</strong> {{ suggestedProduct.available_quantity }} unidades</div>
                <div v-if="suggestedProduct.date"><strong>Fecha:</strong> {{ formatDate(suggestedProduct.date) }}</div>
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="outlined" @click="ignoreStrategyWarning">
            IGNORAR ESTRATEGIA
          </v-btn>
          <v-btn color="success" @click="acceptSuggestedProduct">
            USAR SUGERIDO
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Reporte de Egreso -->
    <v-dialog v-model="showReportModal" max-width="800px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5">Reporte de Salida</span>
          <v-btn
            icon="mdi-printer"
            @click="printReport"
            color="primary"
            variant="text"
            size="small"
          ></v-btn>
        </v-card-title>

        <v-card-text v-if="selectedEgreso" class="report-content">
          <!-- Información del Pedido -->
          <div class="report-header mb-4">
            <h3>Orden de Salida: {{ selectedEgreso.order_number }}</h3>
            <div class="mt-2">
              <strong>Fecha:</strong> {{ formatDate(selectedEgreso.created_at) }}<br>
              <strong>Cliente:</strong> {{ selectedEgreso.customer?.name || 'No especificado' }}<br>
              <strong>RUT:</strong> {{ selectedEgreso.customer?.tax_id || '-' }}<br>
              <strong>Estrategia:</strong> {{ getStrategyText(selectedEgreso.strategy) }}<br>
              <strong>Usuario:</strong> {{ selectedEgreso.user || 'Sistema' }}<br>
              <strong>Notas:</strong> {{ selectedEgreso.notes || 'Sin notas' }}
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <!-- Tabla de Productos -->
          <h4 class="mb-3">Productos</h4>
          <v-table density="compact" class="report-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>SKU</th>
                <th>Cantidad</th>
                <th>Lote</th>
                <th>Vencimiento</th>
                <th>S/N</th>
                <th>F. Fabricación</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in selectedEgreso.items" :key="item.id">
                <td>{{ item.product?.name || 'Producto desconocido' }}</td>
                <td>{{ item.product?.sku || '-' }}</td>
                <td>{{ item.quantity }}</td>
                <td>{{ item.lot_number || '-' }}</td>
                <td>{{ formatDate(item.expiry_date) || '-' }}</td>
                <td>{{ item.serial_number || '-' }}</td>
                <td>{{ formatDate(item.production_date) || '-' }}</td>
              </tr>
            </tbody>
          </v-table>

          <!-- Resumen -->
          <v-divider class="my-4"></v-divider>
          <div class="report-summary">
            <div class="d-flex justify-space-between">
              <strong>Total de SKUs:</strong>
              <span>{{ selectedEgreso.items?.length || 0 }}</span>
            </div>
            <div class="d-flex justify-space-between">
              <strong>Total de Unidades:</strong>
              <span>{{ selectedEgreso.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0 }}</span>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="printReport" prepend-icon="mdi-printer">
            Imprimir
          </v-btn>
          <v-btn color="grey darken-1" text @click="showReportModal = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn
          color="white"
          variant="text"
          @click="snackbar.show = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import ClienteSelector from '@/components/ClienteSelector.vue'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'
import api from '@/api'

export default {
  name: 'Egresos',
  components: {
    ClienteSelector,
    ResponsiveDataTable
  },
  
  data() {
    return {
      // Filtros
      filters: {
        dateFrom: '',
        dateTo: '',
        search: ''
      },
      
      // Estado principal
      egresos: [],
      loading: false,
      exportingCSV: false,
      
      // Datos precargados
      allProducts: [],
      inventoryData: [],
      loadingInitialData: false,
      
      // Modal crear/editar
      showCreateModal: false,
      isEditingEgreso: false,
      formValid: false,
      submitting: false,
      
      // Formulario de egreso
      newEgreso: {
        customer_id: null,
        strategy: 'libre',
        source_warehouse_id: null,  // Bodega de origen (obligatorio)
        notes: '',
        items: []
      },
      
      // Estrategias de picking
      strategyOptions: [
        { 
          value: 'libre', 
          title: 'Libre', 
          subtitle: 'Sin restricciones de estrategia' 
        },
        { 
          value: 'fifo', 
          title: 'FIFO', 
          subtitle: 'Primero en Entrar, Primero en Salir' 
        },
        { 
          value: 'fefo', 
          title: 'FEFO', 
          subtitle: 'Primero en Vencer, Primero en Salir' 
        },
        { 
          value: 'lifo', 
          title: 'LIFO', 
          subtitle: 'Último en Entrar, Primero en Salir' 
        }
      ],
      
      // Escaneo de códigos
      showBarcodeScanner: false,
      isMultipleScanMode: false,
      multipleScanInput: '',
      
      // Advertencias de estrategia
      showStrategyWarningDialog: false,
      strategyWarningMessage: '',
      suggestedProduct: null,
      currentValidatingItem: null, // Item que está siendo validado
      
      // Modal de reporte
      showReportModal: false,
      selectedEgreso: null,
      
      // Notificaciones
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000
      },

      // ========== PARSER GS1 PROFESIONAL - DATOS ==========
      
      // Separador FNC1 (ASCII 29)
      GS: String.fromCharCode(29),
      
      // Definición de AIs (Application Identifiers) según especificaciones del usuario
      AI_DEFS: [
        // ---- Longitud fija ----
        { key: "00",  len: 18, fixed: true,  name: "SSCC" },
        { key: "01",  len: 14, fixed: true,  name: "GTIN" }, // Identificador del producto
        { key: "02",  len: 14, fixed: true,  name: "Content GTIN" },
        { key: "11",  len: 6,  fixed: true,  name: "Prod date (YYMMDD)" }, // Fecha de producción
        { key: "13",  len: 6,  fixed: true,  name: "Pack date (YYMMDD)" }, // Fecha de empaquetado
        { key: "15",  len: 6,  fixed: true,  name: "Best before (YYMMDD)" }, // Fecha de consumo preferente
        { key: "17",  len: 6,  fixed: true,  name: "Expiry (YYMMDD)" }, // Fecha de vencimiento
        { key: "310", len: 6,  fixed: true,  name: "Net weight (kg, 6d)",  wildcard: true }, // 310x
        { key: "320", len: 6,  fixed: true,  name: "Net weight (lb, 6d)", wildcard: true }, // 320x
        { key: "390", len: null, fixed: false, name: "Amount w/o curr",   wildcard: true, max: 15 }, // 390x var
        
        // ---- Longitud variable ----
        { key: "10", fixed: false, name: "Batch/Lot", max: 20 }, // Lote
        { key: "21", fixed: false, name: "Serial",    max: 20 }, // Número de serie
        { key: "22", fixed: false, name: "CPV",       max: 20 }, // Consumer Product Variant
        { key: "30",  fixed: false, name: "Qty variable measure", max: 8 },
        { key: "37",  fixed: false, name: "Count", max: 8 },
        { key: "240", fixed: false, name: "Additional ID", max: 30 },
        { key: "241", fixed: false, name: "Customer Part #", max: 30 },
        { key: "242", fixed: false, name: "MTO variant", max: 6 },
        { key: "243", fixed: false, name: "Supplier Part #", max: 30 },
      ]
    }
  },
  
  computed: {
    filteredEgresos() {
      let filtered = this.egresos
      
      // Filtro por fecha desde
      if (this.filters.dateFrom) {
        filtered = filtered.filter(e => e.created_at >= this.filters.dateFrom)
      }
      
      // Filtro por fecha hasta
      if (this.filters.dateTo) {
        filtered = filtered.filter(e => e.created_at <= this.filters.dateTo)
      }
      
      // Filtro por búsqueda (texto)
      if (this.filters.search && this.filters.search.trim()) {
        const searchTerm = this.filters.search.toLowerCase().trim()
        filtered = filtered.filter(e => {
          // Buscar en múltiples campos
          const orderNumber = (e.order_number || '').toLowerCase()
          const customerName = (e.customer?.name || '').toLowerCase()
          const customerTaxId = (e.customer?.tax_id || '').toLowerCase()
          const strategy = this.getStrategyText(e.strategy).toLowerCase()
          
          return orderNumber.includes(searchTerm) ||
                 customerName.includes(searchTerm) ||
                 customerTaxId.includes(searchTerm) ||
                 strategy.includes(searchTerm)
        })
      }
      
      return filtered
    },
    
    canSaveEgreso() {
      return this.newEgreso.customer_id && 
             this.newEgreso.items.length > 0 && 
             this.formValid
    },
    
    totalQuantity() {
      return this.newEgreso.items.reduce((sum, item) => sum + (item.quantity || 0), 0)
    },
    
    // Nueva: Lista de bodegas para el selector
    warehousesForEgreso() {
      return this.$store.state.warehouses.warehouses || []
    },
    
    // Verificar si se ha seleccionado bodega
    isLocationSelected() {
      return !!this.newEgreso.source_warehouse_id
    }
  },
  
  async created() {
    await this.loadEgresos()
  },
  
  methods: {
    // Cargar datos
    async loadInitialData() {
      if (this.loadingInitialData) return
      
      try {
        this.loadingInitialData = true
        console.log('🔄 Cargando datos iniciales...')
        
        // Cargar productos y inventario en paralelo
        const [productsResponse, inventoryResponse] = await Promise.all([
          api.get('/products'),
          api.get('/inventory')
        ])
        
        this.allProducts = productsResponse.data || []
        this.inventoryData = inventoryResponse.data || []
        
        console.log(`✅ Datos cargados: ${this.allProducts.length} productos, ${this.inventoryData.length} items de inventario`)
        console.log(`🔍 DEBUG - Respuesta completa del endpoint /inventory (primeros 3 items):`, inventoryResponse.data?.slice(0, 3))
        
        // DEBUG: Verificar items del producto específico
        const product1563Items = this.inventoryData.filter(item => item.product?.id === 1563)
        console.log(`🔍 DEBUG - Items del producto 1563 en inventoryData:`, product1563Items.length)
        product1563Items.forEach(item => {
          console.log(`  - Item ID: ${item.id}, Lote: ${item.lot_number}, Vencimiento: ${item.expiry_date}, Cantidad: ${item.quantity}, Warehouse: ${item.location?.warehouse?.name}`)
        })
        
        // DEBUG: Verificar si existe el lote F2448758 en algún producto
        const f2448758Items = this.inventoryData.filter(item => item.lot_number === 'F2448758')
        console.log(`🔍 DEBUG - Items con lote F2448758 en inventoryData:`, f2448758Items.length)
        f2448758Items.forEach(item => {
          console.log(`  - Item ID: ${item.id}, Producto: ${item.product?.name} (${item.product?.id}), Lote: ${item.lot_number}, Vencimiento: ${item.expiry_date}, Cantidad: ${item.quantity}`)
        })
        
        // DEBUG: Verificar productos con GTIN 27640121880562
        const gtin27640121880562Products = this.allProducts.filter(p => p.gtin === '27640121880562')
        console.log(`🔍 DEBUG - Productos con GTIN 27640121880562:`, gtin27640121880562Products.length)
        gtin27640121880562Products.forEach(p => {
          console.log(`  - Producto ID: ${p.id}, Nombre: ${p.name}, SKU: ${p.sku}, GTIN: ${p.gtin}`)
        })
        
        // DEBUG: Buscar productos llamados "SENSA-MAR"
        const sensaMarProducts = this.allProducts.filter(p => p.name?.includes('SENSA-MAR'))
        console.log(`🔍 DEBUG - Productos con nombre "SENSA-MAR":`, sensaMarProducts.length)
        sensaMarProducts.forEach(p => {
          console.log(`  - Producto ID: ${p.id}, Nombre: ${p.name}, SKU: ${p.sku}, GTIN: ${p.gtin}`)
        })
        
      } catch (error) {
        console.error('❌ Error cargando datos iniciales:', error)
        this.showError('Error al cargar datos del sistema')
      } finally {
        this.loadingInitialData = false
      }
    },
    
    async loadEgresos() {
      try {
        this.loading = true
        const response = await api.get('/egresos/egress-orders', {
          params: this.filters
        })
        this.egresos = response.data || []
      } catch (error) {
        console.error('Error loading egresos:', error)
        this.showError('Error al cargar órdenes de egreso')
      } finally {
        this.loading = false
      }
    },
    
    // Modal handlers
    async showNewEgresoModal() {
      this.resetEgresoForm()
      this.isEditingEgreso = false
      this.showCreateModal = true
      
      // Cargar bodegas
      await this.$store.dispatch('warehouses/fetchWarehouses')
      
      // Cargar datos necesarios para el escaneo
      await this.loadInitialData()
    },
    
    closeCreateModal() {
      this.showCreateModal = false
      this.resetEgresoForm()
    },
    
    resetEgresoForm() {
      this.newEgreso = {
        customer_id: null,
        strategy: 'libre',
        source_warehouse_id: null,
        location_id: null,
        notes: '',
        items: []
      }
      this.formValid = false
      this.closeBarcodeScanner()
    },
    
    // Customer handlers
    onCustomerSelected(customer) {
      console.log('Customer selected:', customer)
    },
    
    onCustomerCreated(customer) {
      console.log('Customer created:', customer)
      this.showSuccess(`Cliente ${customer.name} creado exitosamente`)
    },
    
    // Strategy handlers
    onStrategyChanged(strategy) {
      console.log('Strategy changed:', strategy)
      // Revalidar todos los productos cuando cambie la estrategia
      if (this.newEgreso.items.length > 0) {
        this.validateAllProductStrategies()
      }
    },
    
    // Manejar cambio de bodega
    async onWarehouseChanged(warehouseId) {
      console.log('📍 Bodega cambiada:', warehouseId)
      
      // Limpiar productos agregados (por precaución)
      if (this.newEgreso.items.length > 0) {
        if (confirm('Al cambiar la bodega se eliminarán los productos agregados. ¿Desea continuar?')) {
          this.newEgreso.items = []
        } else {
          // Revertir cambio de bodega
          this.$nextTick(() => {
            const previousWarehouse = this.newEgreso.source_warehouse_id
            this.newEgreso.source_warehouse_id = previousWarehouse
          })
          return
        }
      }
      
      // Resetear location_id - se determinará cuando se agregue el primer producto
      // basándonos en dónde está realmente el stock
      this.newEgreso.location_id = null
      console.log('📍 Location_id reseteado - se determinará al agregar productos')
    },
    
    async validateAllProductStrategies() {
      for (let item of this.newEgreso.items) {
        await this.validateProductStrategy(item)
      }
    },
    
    async validateProductStrategy(item) {
      if (this.newEgreso.strategy === 'libre') {
        item.strategy_compliant = true
        item.strategy_warning = null
        return
      }
      
      try {
        console.log(`🔍 Validando estrategia ${this.newEgreso.strategy} para producto ${item.product_id}`)
        
        // Obtener items disponibles del producto
        const response = await api.get(`/inventory/products/${item.product_id}/available-items`)
        const availableItems = response.data
        
        console.log('📦 Items disponibles:', availableItems)
        
        let compliant = true
        let warning = null
        let suggestedItem = null
        
        if (this.newEgreso.strategy === 'fifo') {
          ({ compliant, warning, suggestedItem } = this.validateFIFOStrategy(item, availableItems))
        } else if (this.newEgreso.strategy === 'lifo') {
          ({ compliant, warning, suggestedItem } = this.validateLIFOStrategy(item, availableItems))
        } else if (this.newEgreso.strategy === 'fefo') {
          // Solo validar FEFO si hay fecha de vencimiento
          if (!item.expiry_date) {
            console.log('⚠️ FEFO: No hay fecha de vencimiento, saltando validación')
            compliant = true
            warning = null
            suggestedItem = null
          } else {
            ({ compliant, warning, suggestedItem } = this.validateFEFOStrategy(item, availableItems))
          }
        }
        
        item.strategy_compliant = compliant
        item.strategy_warning = warning
        item.suggested_product = suggestedItem
        
        if (!compliant) {
          console.log(`⚠️ Estrategia no cumplida: ${warning}`)
        } else {
          console.log(`✅ Estrategia cumplida para ${this.newEgreso.strategy}`)
        }
        
      } catch (error) {
        console.error('❌ Error validando estrategia:', error)
        item.strategy_compliant = true // En caso de error, permitir el producto
      }
    },
    
    // Barcode scanning
    startSingleScan() {
      this.isMultipleScanMode = false
      this.showBarcodeScanner = true
      this.multipleScanInput = ''
      
      // Enfocar automáticamente el input después de mostrar el scanner
      this.focusScannerInput()
    },
    
    // Método reutilizable para enfocar el input del escáner
    focusScannerInput() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.scannerInput && this.$refs.scannerInput.$el) {
            const textarea = this.$refs.scannerInput.$el.querySelector('textarea')
            if (textarea) {
              textarea.focus()
              console.log('✅ Scanner input focused')
            } else {
              console.log('❌ Textarea element not found')
            }
          } else {
            console.log('❌ Scanner input ref not found')
          }
        }, 100)
      })
    },
    
    startMultipleScan() {
      this.isMultipleScanMode = true
      this.showBarcodeScanner = true
      this.multipleScanInput = ''
    },
    
    closeBarcodeScanner() {
      this.showBarcodeScanner = false
      this.isMultipleScanMode = false
      this.multipleScanInput = ''
    },
    
    clearMultipleScanInput() {
      this.multipleScanInput = ''
    },
    
    async processMultipleCodes() {
      const codes = this.multipleScanInput.split('\n').filter(code => code.trim())
      
      for (const code of codes) {
        await this.processScannedBarcode(code.trim())
      }
      
      this.closeBarcodeScanner()
    },
    
    async processScannedBarcode(code) {
      if (!code || !code.trim()) return
      
      // Normalizar el código: eliminar espacios y saltos de línea
      const cleanedCode = code.trim()
      console.log(`🔍 Procesando código de barras: ${cleanedCode}`)
      
      // Verificar que los datos estén cargados
      if (this.allProducts.length === 0) {
        console.log('⚠️ Los datos no están cargados, cargando...')
        await this.loadInitialData()
      }
      
      try {
        // Analizar el código
        const analysis = this.analyzeBarcode(cleanedCode)
        console.log('📊 Análisis del código:', analysis)
        
        let product = null
        let productData = {}
        
        if (analysis.type === 'GS1') {
          console.log(`🏷️ Código GS1 detectado, GTIN: ${analysis.gtin}`)
          // Buscar producto por GTIN
          product = this.findProductByGTIN(analysis.gtin)
          productData = analysis.variableData || {}
          console.log('📦 Datos variables extraídos:', productData)
        } else {
          console.log(`📋 Código estándar detectado: ${cleanedCode}`)
          // Buscar producto por código de barras normal (usando código limpio)
          product = this.findProductByBarcode(cleanedCode)
        }
        
        if (product) {
          console.log(`✅ Producto encontrado: ${product.name} (ID: ${product.id})`)
          await this.addScannedProduct(product, productData, cleanedCode)
        } else {
          console.log(`❌ Producto NO encontrado para código: ${cleanedCode}`)
          console.log(`🔍 Productos disponibles: ${this.allProducts.length}`)
          if (this.allProducts.length > 0) {
            console.log('📝 Primeros 5 códigos de barras en productos:', 
              this.allProducts.slice(0, 5).map(p => p.barcode))
          }
          this.showError(`Producto no encontrado para el código: ${cleanedCode}`)
        }
        
      } catch (error) {
        console.error('❌ Error procesando código:', error)
        this.showError(`Error procesando código: ${cleanedCode}`)
      }
      
      // Limpiar input en modo individual
      if (!this.isMultipleScanMode) {
        this.multipleScanInput = ''
      }
    },
    
    // ========== MÉTODOS DE ANÁLISIS DE CÓDIGOS DE BARRAS ==========
    
    // Detectar tipo de código de barras y extraer GTIN si es GS1
    analyzeBarcode(barcode) {
      if (!barcode || barcode.trim() === '') {
        return { type: 'Sin código', gtin: null, original: barcode, variableData: null }
      }
      
      const cleaned = barcode.trim()
      
      console.log('🧪 Analizando código:', {
        original: cleaned,
        length: cleaned.length,
        startsWithAI01: cleaned.startsWith('01'),
        hasParens: cleaned.includes('(')
      })
      
      // Detección GS1 mejorada - según especificaciones del usuario
      const isGS1 = cleaned.includes('(') || 
                   (cleaned.startsWith('01') && cleaned.length > 16) ||
                   (cleaned.length > 20 && /^\d/.test(cleaned)) ||
                   (cleaned.includes(String.fromCharCode(29))) // Incluir códigos con separador FNC1
      
      if (isGS1) {
        const { gtin, variableData } = this.extractGTINAndVariableData(cleaned)
        const hasVariableData = variableData && Object.keys(variableData).length > 0
        
        console.log('✅ Identificado como GS1:', {
          gtin: gtin,
          hasVariableData: hasVariableData,
          variableData: variableData
        })
        
        return { 
          type: 'GS1', 
          gtin: gtin,
          original: cleaned,
          variableData: variableData,
          hasVariableData: hasVariableData
        }
      }
      
      // Solo evaluar códigos de longitud fija si NO es GS1
      // EAN-13 (exactamente 13 dígitos)
      if (/^\d{13}$/.test(cleaned)) {
        console.log('📊 Identificado como EAN-13:', cleaned)
        return { type: 'EAN-13', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      // UPC-A (exactamente 12 dígitos)
      if (/^\d{12}$/.test(cleaned)) {
        console.log('📊 Identificado como UPC-A:', cleaned)
        return { type: 'UPC-A', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      // ITF-14 (exactamente 14 dígitos)
      if (/^\d{14}$/.test(cleaned)) {
        console.log('📊 Identificado como ITF-14:', cleaned)
        return { type: 'ITF-14', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      // Otros códigos
      console.log('📋 Código no identificado específicamente:', cleaned)
      return { type: 'Other', barcode: cleaned, original: cleaned, variableData: null }
    },
    
    // Extraer GTIN y datos variables de código GS1
    extractGTINAndVariableData(gs1Code) {
      if (!gs1Code) return { gtin: null, variableData: null }
      
      let cleaned = gs1Code.replace(/[()]/g, '') // Remover paréntesis si existen
      cleaned = this.normalizeFNC1(cleaned)      // Normalizar FNC1 desde escáner DS2278
      let gtin = null
      let variableData = {}
      
      console.log('🔧 Extrayendo GTIN de:', cleaned)
      console.log('🔧 Código después de normalizar FNC1:', cleaned.replace(new RegExp(this.GS, 'g'), '<FNC1>'))
      
      // Log del código normalizado para debug
      if (window.debugGS1) {
        console.log('🚨 DEBUG GS1: Código detectado')
        console.log('Código completo:', cleaned)
        console.log('¿Contiene FNC1?', cleaned.includes(this.GS))
      }
      
      try {
        // Método mejorado según especificaciones del usuario:
        // "Si el código empieza con 01 los siguientes 14 caracteres son el GTIN"
        
        if (cleaned.startsWith('01') && cleaned.length >= 16) {
          // Extraer exactamente los siguientes 14 caracteres después de "01"
          gtin = cleaned.substring(2, 16) // Posiciones 2-15 (14 dígitos del GTIN)
          const remainingData = cleaned.substring(16) // Todo lo que viene después del GTIN
          
          console.log('✅ GTIN extraído según especificación (01 + 14 chars):', gtin)
          console.log('🔍 Datos restantes para parsing:', remainingData)
          
          if (remainingData && remainingData.length > 0) {
            // Parsear datos variables que vienen después del GTIN
            variableData = this.parseGS1VariableData(remainingData)
          }
        } else {
          // Métodos alternativos para otros casos
          const gtinMatch = cleaned.match(/^01(\d{14})(.*)/)
          if (gtinMatch) {
            gtin = gtinMatch[1] // GTIN de 14 dígitos
            const remainingData = gtinMatch[2] // Datos adicionales después del GTIN
            
            console.log('✅ GTIN extraído (regex):', gtin)
            console.log('🔍 Datos restantes para parsing:', remainingData)
            
            if (remainingData && remainingData.length > 0) {
              variableData = this.parseGS1VariableData(remainingData)
            }
          } else if (/^\d+$/.test(cleaned) && cleaned.length > 14) {
            // Si es un código largo sin AI visible, intentar extraer los primeros 14 dígitos
            gtin = cleaned.substring(0, 14)
            console.log('⚠️ GTIN extraído por longitud:', gtin)
          }
        }
        
        // Si no se puede extraer GTIN, usar el código original
        if (!gtin) {
          console.log('❌ No se pudo extraer GTIN, usando código completo')
          gtin = cleaned
        }
        
      } catch (error) {
        console.error('❌ Error parsing GS1 code:', error)
        gtin = cleaned
      }
      
      console.log('🎯 Resultado final - GTIN:', gtin, 'Variables:', variableData)
      return { gtin, variableData }
    },
    
    // ========== PARSER GS1 PROFESIONAL ==========
    
    // Función de utilidad para parsear fechas GS1 (YYMMDD → YYYY-MM-DD)
    parseGS1Date(dateValue, aiType = 'genérica') {
      if (!dateValue || dateValue.length !== 6) {
        console.warn(`⚠️ Fecha GS1 inválida (${aiType}):`, dateValue)
        return null
      }
      
      const yy = dateValue.substring(0, 2)
      const mm = dateValue.substring(2, 4)
      let dd = dateValue.substring(4, 6)
      
      // Validar rangos básicos
      const monthNum = parseInt(mm)
      const dayNum = parseInt(dd)
      
      if (monthNum < 1 || monthNum > 12) {
        console.warn(`⚠️ Mes inválido en fecha GS1 (${aiType}):`, dateValue, `→ mm=${mm}`)
        return null
      }
      
      // Corregir día 00 a 01 para fechas válidas
      if (dd === '00') {
        dd = '01'
        console.log(`📅 Corrigiendo día 00 → 01 en fecha ${aiType}: ${dateValue} → ${yy}${mm}${dd}`)
      }
      
      // Validar día después de corrección
      if (dayNum < 0 || dayNum > 31) {
        console.warn(`⚠️ Día inválido en fecha GS1 (${aiType}):`, dateValue, `→ dd=${dd}`)
        return null
      }
      
      const parsedDate = `20${yy}-${mm}-${dd}T00:00:00`
      console.log(`📅 Fecha GS1 parseada (${aiType}): ${dateValue} → ${parsedDate}`)
      return parsedDate
    },
    
    // Construir índices de búsqueda
    buildAIIndex() {
      const exact = new Map()
      const wild = []
      
      for (const d of this.AI_DEFS) {
        if (d.wildcard) {
          const re = new RegExp("^" + d.key + "\\d")
          wild.push({ re, def: d })
        } else {
          exact.set(d.key, d)
        }
      }
      
      return { exact, wild }
    },
    
    // Encontrar AI en posición específica
    findAI(s, pos, aiIndex) {
      const chunk4 = s.slice(pos, pos + 4)
      const chunk3 = s.slice(pos, pos + 3)
      const chunk2 = s.slice(pos, pos + 2)
      
      console.log(`🔍 findAI en pos ${pos}: chunk2="${chunk2}", chunk3="${chunk3}", chunk4="${chunk4}"`)
      
      // Wildcards primero (310x, 320x, 390x, etc)
      for (const { re, def } of aiIndex.wild) {
        if (re.test(s.slice(pos, pos + 4))) {
          console.log(`✅ AI wildcard encontrado: ${s.slice(pos, pos + 4)} (base: ${def.key})`)
          return { key: s.slice(pos, pos + 4), base: def.key, def }
        }
      }
      
      // Exactos 4 → 3 → 2 dígitos
      if (aiIndex.exact.has(chunk4)) {
        console.log(`✅ AI exacto 4 dígitos encontrado: ${chunk4}`)
        return { key: chunk4, base: chunk4, def: aiIndex.exact.get(chunk4) }
      }
      if (aiIndex.exact.has(chunk3)) {
        console.log(`✅ AI exacto 3 dígitos encontrado: ${chunk3}`)
        return { key: chunk3, base: chunk3, def: aiIndex.exact.get(chunk3) }
      }
      if (aiIndex.exact.has(chunk2)) {
        console.log(`✅ AI exacto 2 dígitos encontrado: ${chunk2}`)
        return { key: chunk2, base: chunk2, def: aiIndex.exact.get(chunk2) }
      }
      
      console.log(`❌ No se encontró AI en pos ${pos}`)
      return null
    },
    
    // Validar si una secuencia AI es realmente válida - Simplificado con regla de AI único
    isValidAISequence(aiMatch, remainingData) {
      if (!aiMatch || !remainingData) return false
      
      const { def } = aiMatch
      
      console.log(`🔍 Validando AI ${aiMatch.key} con datos: "${remainingData.substring(0, 15)}..."`)
      
      // Para AIs de longitud fija, verificar que hay suficiente contenido
      if (def.fixed) {
        const requiredLength = def.len
        if (remainingData.length < requiredLength) {
          console.log(`❌ AI ${aiMatch.key} requiere ${requiredLength} chars, solo quedan ${remainingData.length}`)
          return false
        }
        
        // Para fechas, verificar formato básico (6 dígitos)
        if (['11', '13', '15', '17'].includes(def.key) && !/^\d{6}/.test(remainingData)) {
          console.log(`❌ AI fecha ${aiMatch.key} no tiene formato válido:`, remainingData.substring(0, 6))
          return false
        }
        
        console.log(`✅ AI fijo ${aiMatch.key} válido`)
        return true
      }
      
      // Para AIs de longitud variable
      if (remainingData.length === 0) {
        console.log(`❌ AI variable ${aiMatch.key} sin contenido`)
        return false
      }
      
      // Validación especial para AI (01) GTIN
      if (aiMatch.base === '01') {
        if (remainingData.length < 14 || !/^\d{14}/.test(remainingData)) {
          console.log(`❌ AI (01) debe tener 14 dígitos, encontrado:`, remainingData.substring(0, 14))
          return false
        }
        
        const afterGTIN = remainingData.substring(14)
        if (afterGTIN.length > 0) {
          const nextPossibleAI = afterGTIN.substring(0, 2)
          const validNextAIs = ['10', '11', '13', '15', '17', '21', '30', '37']
          
          if (!validNextAIs.includes(nextPossibleAI) && afterGTIN.length > 2) {
            console.log(`❌ AI (01) seguido de secuencia inválida: "${nextPossibleAI}"`)
            return false
          }
        }
      }
      
      // Para AI (10) lote, verificar contenido mínimo razonable
      if (aiMatch.base === '10') {
        if (remainingData.length <= 2) {
          console.log(`❌ AI (10) con contenido muy corto: "${remainingData}"`)
          return false
        }
      }
      
      console.log(`✅ AI variable ${aiMatch.key} válido`)
      return true
    },
    
    // Normalizar distintos formatos de FNC1 a un único \x1D
    normalizeFNC1(s) {
      if (!s) return s;
      
      if (window.debugGS1) {
        console.log('🔧 normalizeFNC1 input:', s);
      }
      
      let result = s
        .replace(/\u001D/g, this.GS)                   // FNC1 real (ASCII 29)
        .replace(/<FNC1>|<GS>|\|/gi, this.GS);         // si usaste texto visible
      
      // Parche mejorado: el lector está enviando "0029". 
      // Convertir SOLO cuando aparece como separador entre un valor y el siguiente AI válido
      const before = result;
      result = result.replace(/(\d{3,30})0029(?=(11|13|15|17|10|21|22|240|241|242|243|30|37))/g, `$1${this.GS}`);
      
      if (before !== result) {
        console.log('✅ Se aplicaron reemplazos de 0029:', {
          before: before,
          after: result.replace(new RegExp(this.GS, 'g'), '<FNC1>')
        });
      } else {
        console.log('❌ NO se aplicaron reemplazos de 0029');
        
        // Intentar método alternativo: buscar patrones específicos
        if (s.includes('0029')) {
          console.log('🔧 Intentando método alternativo para 0029...');
          // Buscar patrones más específicos
          const alternativePattern = /(\d{3,30})0029(\d{2,4})/g;
          let match;
          while ((match = alternativePattern.exec(s)) !== null) {
            const [full, beforeValue, afterValue] = match;
            console.log('📍 Patrón encontrado:', {
              full: full,
              before: beforeValue,
              after: afterValue,
              afterLength: afterValue.length
            });
            
            // Verificar si el "after" parece un AI válido
            const commonAIs = ['10', '11', '13', '15', '17', '21', '30', '37'];
            const possibleAI = afterValue.substring(0, 2);
            if (commonAIs.includes(possibleAI)) {
              console.log('✅ Detectado AI válido:', possibleAI);
              result = result.replace(full, `${beforeValue}${this.GS}${afterValue}`);
            }
          }
        }
      }
      
      console.log('🎯 normalizeFNC1 result:', result.replace(new RegExp(this.GS, 'g'), '<FNC1>'));
      return result;
    },

    // Parser GS1 completo con regla: cada AI aparece UNA SOLA VEZ
    parseGS1(raw) {
      if (!raw) return { items: [], warnings: ["Empty input"] }
      
      // Normalización mejorada para DS2278
      let s = String(raw).trim()
      s = s.replace(/^\](C1|e0|E0|d2|Q3)/i, "")   // quitar prefijos de simbología
      s = this.normalizeFNC1(s)                   // normalizar FNC1 desde escáner DS2278
      s = s.replace(/<FNC1>|<GS>/gi, this.GS)    // algunos lectores usan textos literales
      
      console.log('🔍 Parseando GS1 completo:', s.replace(new RegExp(this.GS, 'g'), '<FNC1>'))
      
      const aiIndex = this.buildAIIndex()
      const items = []
      const warnings = []
      const processedAIs = new Set() // Registro de AIs ya procesados
      let i = 0
      
      while (i < s.length) {
        console.log(`🔍 Posición ${i}: Analizando "${s.slice(i, i+8)}..."`)
        
        // Saltar separadores FNC1
        if (s[i] === this.GS) { 
          i++
          console.log('⚡ FNC1 encontrado y saltado')
          continue 
        }
        
        const m = this.findAI(s, i, aiIndex)
        if (!m) {
          // MEJORA: Si no encontramos un AI válido, pero hay caracteres restantes,
          // intentar buscar AI 10 implícito (lote que puede venir sin prefijo explícito)
          if (s.length > i && !processedAIs.has('10')) {
            const remaining = s.slice(i)
            // Si los caracteres restantes parecen un lote (alfanuméricos, longitud razonable)
            if (/^[A-Za-z0-9]{3,20}/.test(remaining)) {
              console.log(`🔍 AI 10 implícito detectado: "${remaining.substring(0, 10)}..."`)
              // Crear un AI 10 artificial
              const lotValue = remaining.match(/^[A-Za-z0-9]{3,20}/)[0]
              items.push({ ai: '10', aiBase: '10', name: 'Batch/Lot', value: lotValue, ambiguous: false })
              processedAIs.add('10')
              i += lotValue.length
              continue
            }
          }
          
          warnings.push(`AI desconocido en posición ${i}: "${s.slice(i, i+4)}..."`)
          console.log(`⚠️ AI desconocido en posición ${i}:`, s.slice(i, i+4))
          console.log(`🔍 Caracteres siguientes: "${s.slice(i, i+8)}"`)
          break
        }
        
        const { key, base, def } = m
        
        // REGLA CRÍTICA: Solo procesar AI 17 (vencimiento) y AI 10 (lote)
        // Ignorar todos los demás AIs (incluyendo AI 30 quantity)
        const criticalAIs = ['10', '17']
        if (!criticalAIs.includes(base)) {
          console.log(`⏭️ Ignorando AI ${key} (${def.name}) - Solo procesamos lote y vencimiento`)
          // Saltar este AI completamente: avanzar posición pero no procesar
          i += key.length
          
          // Si es de longitud fija, saltar también su valor
          if (def.fixed) {
            const need = def.len
            if (i + need <= s.length) {
              i += need
            } else {
              i = s.length
            }
          } else {
            // Para variables, buscar activamente el siguiente AI crítico (10 o 17)
            // en lugar de solo saltar hasta el máximo
            const max = def.max ?? 30
            let j = i
            let foundCriticalAI = false
            
            // Buscar AIs críticos en las siguientes posiciones (incluyendo dentro del valor del AI ignorado)
            // Esto permite detectar lotes que vienen sin prefijo explícito
            while (j < s.length && (j - i) < max && s[j] !== this.GS) {
              // Verificar si en esta posición hay un AI crítico
              const nextAI = this.findAI(s, j, aiIndex)
              if (nextAI && criticalAIs.includes(nextAI.base) && !processedAIs.has(nextAI.base)) {
                console.log(`🎯 Encontrado AI crítico ${nextAI.key} después de ignorar AI ${key}, cortando en posición ${j}`)
                i = j // Saltar hasta donde empieza el AI crítico
                foundCriticalAI = true
                break
              }
              
              // MEJORA: También buscar lotes implícitos (sin prefijo 10) dentro del valor del AI ignorado
              // Esto maneja casos como "302410F2448758" donde el lote "F2448758" viene después del quantity
              if (!processedAIs.has('10') && (j - i) >= 3) {
                // Buscar patrones que parezcan lotes (alfanuméricos, empezando con letra o número)
                const remaining = s.slice(j)
                if (/^[A-Za-z0-9]{4,20}/.test(remaining)) {
                  // Verificar que no sea parte de un AI válido
                  const testAI = this.findAI(s, j, aiIndex)
                  if (!testAI) {
                    // Podría ser un lote implícito, pero solo lo tomamos si no hay más AIs críticos después
                    // Por ahora, continuamos buscando
                  }
                }
              }
              
              j++
            }
            
            // Si no encontramos AI crítico, buscar lotes implícitos dentro del valor del AI ignorado
            if (!foundCriticalAI) {
              // Buscar patrones de lote que empiecen con letra (ej: F2448758)
              // Esto maneja casos donde el lote viene sin prefijo 10 dentro del valor del AI ignorado
              let lotStartPos = -1
              for (let k = i + 1; k < j && k < s.length; k++) {
                // Buscar patrones que empiecen con letra seguida de alfanuméricos
                const testStr = s.slice(k)
                if (/^[A-Za-z][A-Za-z0-9]{3,19}/.test(testStr)) {
                  // Verificar que no sea parte de un AI válido
                  const testAI = this.findAI(s, k, aiIndex)
                  if (!testAI && !processedAIs.has('10')) {
                    lotStartPos = k
                    console.log(`🔍 Posible lote implícito encontrado en posición ${k}: "${testStr.substring(0, 10)}..."`)
                    break
                  }
                }
              }
              
              if (lotStartPos > 0) {
                // Encontramos un lote implícito, saltar hasta ahí
                i = lotStartPos
                const remaining = s.slice(i)
                const lotValue = remaining.match(/^[A-Za-z][A-Za-z0-9]{3,19}/)[0]
                console.log(`✅ Lote implícito detectado después de ignorar AI ${key}: "${lotValue}"`)
                items.push({ ai: '10', aiBase: '10', name: 'Batch/Lot', value: lotValue, ambiguous: false })
                processedAIs.add('10')
                i += lotValue.length
                continue
              }
              
              // Si no encontramos lote implícito, saltar hasta el máximo o FNC1
              i = j
              if (s[i] === this.GS) i++ // Consumir FNC1 si existe
              
              // MEJORA: Después de ignorar un AI, verificar si los caracteres siguientes
              // son un lote implícito (AI 10 sin prefijo)
              if (!processedAIs.has('10') && i < s.length) {
                const remaining = s.slice(i)
                // Si los caracteres restantes parecen un lote (alfanuméricos, longitud razonable)
                if (/^[A-Za-z0-9]{3,20}/.test(remaining)) {
                  // Verificar que no empiece con un AI válido crítico
                  const nextAI = this.findAI(s, i, aiIndex)
                  if (!nextAI || !criticalAIs.includes(nextAI.base)) {
                    console.log(`🔍 AI 10 implícito detectado después de ignorar AI ${key}: "${remaining.substring(0, 10)}..."`)
                    const lotValue = remaining.match(/^[A-Za-z0-9]{3,20}/)[0]
                    items.push({ ai: '10', aiBase: '10', name: 'Batch/Lot', value: lotValue, ambiguous: false })
                    processedAIs.add('10')
                    i += lotValue.length
                    continue
                  }
                }
              }
            }
          }
          continue
        }
        
        i += key.length
        
        // REGLA FUNDAMENTAL: Verificar si este AI ya fue procesado
        if (processedAIs.has(base)) {
          console.log(`⚠️ AI ${base} duplicado detectado - IGNORANDO (regla: un AI por código)`)
          warnings.push(`AI duplicado ${base} ignorado`)
          break // O continuar hasta el final del string como contenido del AI anterior
        }
        
        console.log(`📊 Procesando AI ${key} (${def.name}) - Primer encuentro ✓`)
        processedAIs.add(base) // Marcar como procesado
        
        let value = ""
        let ambiguous = false
        
        if (def.fixed) {
          // Longitud fija
          const need = def.len
          if (i + need > s.length) {
            value = s.slice(i)
            warnings.push(`Valor incompleto para (${key}) esperaba ${need} chars.`)
            i = s.length
          } else {
            value = s.slice(i, i + need)
            i += need
          }
        } else {
          // Longitud variable: SOLO cortar con FNC1 real o límite máximo
          const max = def.max ?? 30
          let j = i
          let cutByFNC1 = false
          
          // PRIORIDAD: Para AI 30 (quantity), ser menos estricto y buscar AIs importantes siguientes
          // AI 30 no es crítico, así que podemos cortarlo temprano si encontramos AI 17 o AI 10
          const isQuantityAI = base === '30'
          const priorityAIs = ['10', '17'] // Lote y vencimiento son prioritarios
          
          while (j < s.length && (j - i) < max) {
            // REGLA ESTRICTA: Solo cortar con FNC1 real
            if (s[j] === this.GS) { 
              console.log(`✂️ FNC1 encontrado, cortando en posición ${j}`)
              cutByFNC1 = true
              break 
            }
            
            // MEJORA: Para AI 30, buscar AIs prioritarios siguientes antes de llegar al límite
            if (isQuantityAI && (j - i) >= 3) { // Mínimo 3 caracteres para quantity
              // Buscar AIs prioritarios (10, 17) en las siguientes posiciones
              // Buscar más adelante para encontrar AI 10 que puede venir después del valor del AI 30
              for (let checkPos = j; checkPos < Math.min(j + 15, s.length - 1); checkPos++) {
                const nextAI = this.findAI(s, checkPos, aiIndex)
                if (nextAI && priorityAIs.includes(nextAI.base) && !processedAIs.has(nextAI.base)) {
                  const remainingAfterAI = s.slice(checkPos + nextAI.key.length)
                  if (this.isValidAISequence(nextAI, remainingAfterAI)) {
                    console.log(`✂️ AI 30: Detectado AI prioritario "${nextAI.key}" en posición ${checkPos}, cortando en ${j}`)
                    j = checkPos
                    break
                  }
                }
              }
              // Si encontramos un AI válido, cortar aquí
              if (j < s.length) {
                const foundAI = this.findAI(s, j, aiIndex)
                if (foundAI && !processedAIs.has(foundAI.base)) {
                  break
                }
              }
            }
            
            j++
          }
          
          // Si no hay FNC1 y no se cortó por AI prioritario, buscar cualquier AI válido siguiente
          if (!cutByFNC1 && j < s.length) {
            // Buscar AI válido siguiente (especialmente importante para AI 30)
            // Para AI 30, buscar más agresivamente el siguiente AI
            const searchStart = isQuantityAI ? Math.max(i + 3, j - 15) : Math.max(i + 3, j - 10)
            const searchEnd = isQuantityAI ? j + 5 : j + 2
            
            for (let k = searchStart; k <= searchEnd && k < s.length - 1; k++) {
              const next = this.findAI(s, k, aiIndex)
              if (next && !processedAIs.has(next.base)) {
                const remainingAfterAI = s.slice(k + next.key.length)
                
                console.log(`🔍 AI potencial "${next.key}" en posición ${k}`)
                
                if (this.isValidAISequence(next, remainingAfterAI)) {
                  // Para AI 30, ser más agresivo en cortar si encontramos un AI válido
                  if (isQuantityAI || (j - i) >= max) {
                    console.log(`✂️ CORTANDO por AI válido siguiente: "${next.key}"`)
                    j = k
                    break
                  }
                }
              }
            }
            
            // MEJORA ESPECIAL: Si es AI 30 y no encontramos AI siguiente explícito,
            // pero hay caracteres restantes que parecen un lote (empiezan con letra o número),
            // limitar el AI 30 a un máximo razonable (8 caracteres según especificación)
            if (isQuantityAI && (j - i) >= 8 && j < s.length) {
              // AI 30 tiene máximo 8 caracteres según especificación
              // Si ya tenemos 8 caracteres y hay más datos, probablemente es otro AI
              console.log(`✂️ AI 30 alcanzó límite de 8 caracteres, cortando`)
              j = i + 8
            }
          }
          
          // Si no hay FNC1, tomar todo hasta el límite máximo o fin del string
          console.log(`📏 Campo variable sin FNC1: tomando hasta posición ${j} (${j-i} chars)`)
          
          value = s.slice(i, j)
          i = j
          if (s[i] === this.GS) i++ // Consumir FNC1 si lo había
          
          // Detectar ambigüedad solo si no fue cortado por FNC1
          if (!cutByFNC1) {
            const nxt = this.findAI(s, i, aiIndex)
            if (nxt && nxt.def && !nxt.def.fixed && !processedAIs.has(nxt.base)) {
              ambiguous = true
            }
          }
        }
        
        console.log(`✅ ${def.name} extraído: "${value}"${ambiguous ? ' (ambiguous)' : ''}`)
        items.push({ ai: key, aiBase: base, name: def.name, value, ambiguous })
      }
      
      console.log(`🎯 AIs procesados: ${Array.from(processedAIs).join(', ')}`)
      return { items, warnings }
    },
    
    parseGS1VariableData(remainingData) {
      const variableData = {}
      
      console.log('🔍 Parseando datos GS1 con parser profesional:', remainingData)
      
      try {
        // Usar el parser completo
        const result = this.parseGS1(remainingData)
        
        if (result.warnings.length > 0) {
          console.warn('⚠️ Advertencias GS1:', result.warnings)
        }
        
        // Variables temporales para fechas
        let expiryDate = null
        let productionDate = null
        let packagingDate = null
        let bestBeforeDate = null
        
        // Convertir items a formato esperado por el resto del código
        for (const item of result.items) {
          switch (item.aiBase) {
            case '10':
              variableData.lot_number = item.value
              break
            case '11':
              // Fecha de producción YYMMDD → YYYY-MM-DD
              productionDate = this.parseGS1Date(item.value, 'producción')
              if (productionDate) {
                variableData.production_date = productionDate
              }
              break
            case '13':
              // Fecha de empaquetado YYMMDD → YYYY-MM-DD
              packagingDate = this.parseGS1Date(item.value, 'empaquetado')
              if (packagingDate) {
                variableData.packaging_date = packagingDate
              }
              break
            case '15':
              // Fecha de consumo preferente YYMMDD → YYYY-MM-DD
              bestBeforeDate = this.parseGS1Date(item.value, 'consumo preferente')
              if (bestBeforeDate) {
                variableData.best_before_date = bestBeforeDate
              }
              break
            case '17':
              // Fecha de vencimiento YYMMDD → YYYY-MM-DD
              expiryDate = this.parseGS1Date(item.value, 'vencimiento')
              if (expiryDate) {
                variableData.expiry_date = expiryDate
              }
              break
            case '21':
              variableData.serial_number = item.value
              break
            case '30':
              variableData.quantity = item.value
              break
            case '37':
              variableData.count = item.value
              break
          }
        }
        
        // Lógica de prioridad para fechas según especificaciones del usuario:
        // Si no hay fecha de vencimiento (17), usar otras fechas en el campo de vencimiento
        if (!expiryDate) {
          if (bestBeforeDate) {
            // Prioridad 1: Best before date (15)
            variableData.expiry_date = bestBeforeDate
            console.log('📅 Usando fecha de consumo preferente (15) como vencimiento:', bestBeforeDate)
          } else if (packagingDate) {
            // Prioridad 2: Packaging date (13) 
            variableData.expiry_date = packagingDate
            console.log('📅 Usando fecha de empaquetado (13) como vencimiento:', packagingDate)
          } else if (productionDate) {
            // Prioridad 3: Production date (11)
            variableData.expiry_date = productionDate
            console.log('📅 Usando fecha de producción (11) como vencimiento:', productionDate)
          }
        }
        
      } catch (error) {
        console.error('❌ Error parsing GS1 variable data:', error)
      }
      
      console.log('🎯 Datos variables extraídos:', variableData)
      return variableData
    },
    
    findProductByGTIN(gtin) {
      console.log(`🔍 Buscando producto por GTIN: ${gtin}`)
      const product = this.allProducts.find(p => p.barcode === gtin)
      if (product) {
        console.log(`✅ Producto encontrado por GTIN: ${product.name} (ID: ${product.id})`)
      } else {
        console.log(`❌ Producto NO encontrado por GTIN: ${gtin}`)
      }
      return product || null
    },
    
    findProductByBarcode(barcode) {
      console.log(`🔍 Buscando producto por código de barras: ${barcode}`)
      const product = this.allProducts.find(p => p.barcode === barcode)
      if (product) {
        console.log(`✅ Producto encontrado por código: ${product.name} (ID: ${product.id})`)
      } else {
        console.log(`❌ Producto NO encontrado por código: ${barcode}`)
      }
      return product || null
    },
    
    async addScannedProduct(product, variableData, originalCode) {
      console.log(`📦 Agregando producto escaneado: ${product.name} (ID: ${product.id})`)
      console.log(`📋 Variable data:`, variableData)
      
      // 1. VALIDAR INVENTARIO DISPONIBLE (considerando lote específico si existe)
      const inventoryValidation = await this.validateProductInventory(product.id, variableData)
      
      if (!inventoryValidation.hasInventory) {
        // Si hay lotes alternativos disponibles, mostrar sugerencia
        if (inventoryValidation.hasAlternatives && inventoryValidation.suggestedItem) {
          console.log('⚠️ Lote sin stock pero hay alternativas disponibles')
          
          // Crear item temporal para mostrar en el modal
          const tempItem = {
            product_id: product.id,
            product_name: product.name,
            product_sku: product.sku,
            quantity: parseInt(variableData.quantity) || 1,
            lot_number: variableData.lot_number || null,
            expiry_date: variableData.expiry_date || null,
            production_date: variableData.production_date || null,
            serial_number: variableData.serial_number || null,
            barcode_scanned: originalCode,
            original_scanned_code: originalCode, // Para tracking de duplicados
            gtin: variableData.gtin || null,
            strategy_compliant: false,
            strategy_warning: inventoryValidation.message,
            suggested_product: inventoryValidation.suggestedItem
          }
          
          // Guardar temporalmente para procesamiento posterior
          this.currentValidatingItem = tempItem
          
          // Mostrar modal de sugerencia
          this.showStrategyWarning(tempItem)
          return
        } else {
          // No hay stock en la ubicación seleccionada
          let specificInfo = ''
          if (variableData?.lot_number) {
            specificInfo += ` (Lote: ${variableData.lot_number})`
          }
          if (variableData?.expiry_date) {
            specificInfo += ` (Venc: ${this.formatDate(variableData.expiry_date)})`
          }
          
          // Mostrar mensaje con información de otras ubicaciones si existe
          let errorMessage = `El producto "${product.name}"${specificInfo} no tiene inventario disponible en la ubicación seleccionada`
          if (inventoryValidation.otherLocations && inventoryValidation.otherLocations.length > 0) {
            errorMessage += `\n\n📍 Stock disponible en otras ubicaciones:\n`
            inventoryValidation.otherLocations.forEach(loc => {
              errorMessage += `• ${loc.warehouse} - ${loc.zone}: ${loc.quantity} unidades\n`
            })
          }
          
          this.showError(errorMessage)
          return
        }
      }
      
      // 2. DETERMINAR location_id Y VALIDAR STOCK SUFICIENTE ANTES DE AGREGAR/INCREMENTAR
      // Primero determinamos la zona con más stock para poder validar correctamente
      const inventoryValidationForZone = await this.validateProductInventory(product.id, variableData)
      
      if (!inventoryValidationForZone.hasInventory || !inventoryValidationForZone.inventoryItems || inventoryValidationForZone.inventoryItems.length === 0) {
        console.warn('⚠️ No hay inventario disponible para determinar zona')
        return
      }
      
      // Agrupar inventario por zona y calcular stock total por zona
      // IMPORTANTE: Si hay un lote específico, sumar TODAS las cantidades del mismo lote en la misma zona
      // independientemente de la fecha de vencimiento (caso especial: mismo lote, diferentes fechas en misma zona)
      const stockByZone = {}
      inventoryValidationForZone.inventoryItems.forEach(item => {
        if (item.location && item.location.id) {
          const zoneId = item.location.id
          
          if (!stockByZone[zoneId]) {
            stockByZone[zoneId] = {
              zoneId: zoneId,
              zoneName: item.location.zone?.name || item.location.code || `Zona ${zoneId}`,
              totalStock: 0,
              items: []
            }
          }
          
          // Si hay lote específico en variableData, sumar TODAS las cantidades del mismo lote en esta zona
          // independientemente de la fecha de vencimiento (caso especial: mismo lote, diferentes fechas)
          if (variableData?.lot_number) {
            const itemLot = (item.lot_number || '').toUpperCase().trim()
            const scannedLot = (variableData.lot_number || '').toUpperCase().trim()
            if (itemLot === scannedLot) {
              // Mismo lote: sumar independientemente de la fecha de vencimiento
              stockByZone[zoneId].totalStock += item.quantity || 0
              stockByZone[zoneId].items.push(item)
              console.log(`✅ Sumando ${item.quantity} unidades del lote ${itemLot} en zona ${zoneId} (expiry: ${item.expiry_date || 'N/A'})`)
            }
          } else {
            // No hay lote específico: sumar todos los items de la zona
            stockByZone[zoneId].totalStock += item.quantity || 0
            stockByZone[zoneId].items.push(item)
          }
        }
      })
      
      // Encontrar la zona con más stock disponible
      let zoneWithMostStock = null
      let maxStock = 0
      Object.values(stockByZone).forEach(zone => {
        if (zone.totalStock > maxStock) {
          maxStock = zone.totalStock
          zoneWithMostStock = zone
        }
      })
      
      if (!zoneWithMostStock) {
        console.error('❌ No se pudo determinar zona con stock disponible')
        return
      }
      
      // Determinar o actualizar location_id
      if (!this.newEgreso.location_id) {
        this.newEgreso.location_id = zoneWithMostStock.zoneId
        console.log(`📍 Location_id determinado: Zona con más stock disponible - ${zoneWithMostStock.zoneName} (ID: ${zoneWithMostStock.zoneId}, Stock: ${maxStock} unidades)`)
      } else {
        const currentZoneStock = stockByZone[this.newEgreso.location_id]
        if (!currentZoneStock || currentZoneStock.totalStock === 0) {
          this.newEgreso.location_id = zoneWithMostStock.zoneId
          console.warn(`⚠️ Producto no disponible en zona actual. Cambiando a zona con más stock: ${zoneWithMostStock.zoneName} (ID: ${zoneWithMostStock.zoneId})`)
        }
      }
      
      // Validar stock suficiente en la zona seleccionada
      const selectedZoneStock = stockByZone[this.newEgreso.location_id]
      if (!selectedZoneStock) {
        this.showError(`No hay stock disponible en la zona seleccionada para el producto ${product.name}`)
        return
      }
      
      // Calcular cantidad total ya agregada de este producto en el egreso actual
      const quantityAlreadyAdded = this.newEgreso.items
        .filter(item => item.product_id === product.id)
        .reduce((sum, item) => sum + (item.quantity || 0), 0)
      
      const quantityToAdd = parseInt(variableData.quantity) || 1
      const totalQuantityNeeded = quantityAlreadyAdded + quantityToAdd
      
      // Verificar si hay suficiente stock en la zona seleccionada
      if (totalQuantityNeeded > selectedZoneStock.totalStock) {
        // No hay suficiente stock en la zona seleccionada
        const availableInOtherZones = Object.values(stockByZone)
          .filter(zone => zone.zoneId !== this.newEgreso.location_id && zone.totalStock > 0)
          .map(zone => ({
            zoneName: zone.zoneName,
            stock: zone.totalStock
          }))
        
        // Construir información del lote si existe
        // Nota: Si hay lote específico, se suman todas las fechas de vencimiento del mismo lote en la misma zona
        let lotInfo = ''
        if (variableData.lot_number) {
          lotInfo = ' para el mismo lote'
        } else if (variableData.expiry_date) {
          lotInfo = ' para la misma fecha de vencimiento'
        }
        
        let errorMessage = `Stock insuficiente en la zona seleccionada (${selectedZoneStock.zoneName})${lotInfo}.\n\n`
        errorMessage += `Stock disponible en esta zona: ${selectedZoneStock.totalStock} unidades\n`
        errorMessage += `Cantidad solicitada: ${totalQuantityNeeded} unidades\n`
        errorMessage += `Ya agregadas en este egreso: ${quantityAlreadyAdded} unidades\n\n`
        errorMessage += `Debes crear un nuevo egreso para continuar agregando.\n`
        
        if (availableInOtherZones.length > 0) {
          errorMessage += `\nQuedan productos disponibles en otras zonas de la misma bodega:\n`
          availableInOtherZones.forEach(zone => {
            errorMessage += `• ${zone.zoneName}: ${zone.stock} unidades\n`
          })
        }
        
        this.showError(errorMessage)
        return
      }
      
      // 3. VERIFICAR DUPLICADOS (mismo SKU + mismo código escaneado originalmente)
      // Buscar si ya existe un item con el mismo código escaneado (incluso si luego fue corregido por estrategia)
      const duplicateIndex = this.findDuplicateByOriginalCode(product.sku, originalCode)
      if (duplicateIndex !== -1) {
        // Incrementar cantidad del item existente (incluso si fue corregido por estrategia)
        // Ya validamos que hay stock suficiente arriba
        this.newEgreso.items[duplicateIndex].quantity += quantityToAdd
        const existingItem = this.newEgreso.items[duplicateIndex]
        let message = `Cantidad actualizada: ${product.name} (${existingItem.quantity} unidades)`
        
        // Si el item fue corregido por estrategia, informar al usuario
        if (existingItem.lot_number !== variableData.lot_number || 
            existingItem.expiry_date !== variableData.expiry_date) {
          message += ` - Usando lote corregido: ${existingItem.lot_number || 'N/A'}`
        }
        
        this.showSuccess(message)
        console.log(`♻️ Duplicado detectado - Incrementando cantidad a ${existingItem.quantity}`)
        console.log(`📋 Item original escaneado: ${originalCode}`)
        console.log(`🔄 Item actual: Lote ${existingItem.lot_number}, Venc ${existingItem.expiry_date}`)
        return
      }

      // 4. SI NO VIENE LOTE EN EL CÓDIGO Y HAY ESTRATEGIA ACTIVA, APLICAR ESTRATEGIA
      if (!variableData.lot_number && this.newEgreso.strategy !== 'libre') {
        console.log(`📋 No hay lote en código de barras, aplicando estrategia ${this.newEgreso.strategy}`)
        
        // Obtener el lote sugerido según la estrategia y bodega seleccionada
        const suggestedLot = await this.getSuggestedLotByStrategy(product.id)
        
        if (suggestedLot) {
          console.log(`✅ Lote sugerido por estrategia:`, suggestedLot)
          // Rellenar con el lote sugerido
          variableData.lot_number = suggestedLot.lot_number
          variableData.expiry_date = suggestedLot.expiry_date
          variableData.production_date = suggestedLot.production_date
        } else {
          console.log(`⚠️ No se encontró lote sugerido, continuando sin lote`)
        }
      }
      
      // 4. CREAR NUEVO ITEM
      const item = {
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        quantity: parseInt(variableData.quantity) || 1,
        lot_number: variableData.lot_number || null,
        expiry_date: variableData.expiry_date || null,
        production_date: variableData.production_date || null,
        serial_number: variableData.serial_number || null,
        barcode_scanned: originalCode,
        original_scanned_code: originalCode, // Nuevo campo para tracking de duplicados
        gtin: variableData.gtin || null,
        strategy_compliant: true,
        strategy_warning: null
      }
      
      console.log(`🏷️ Item creado:`, {
        sku: item.product_sku,
        quantity: item.quantity,
        lot: item.lot_number,
        expiry: item.expiry_date,
        serial: item.serial_number,
        production: item.production_date
      })
      
      // 5. VALIDAR ESTRATEGIA
      await this.validateProductStrategy(item)
      
      // 6. AGREGAR AL LISTADO
      this.newEgreso.items.push(item)
      
      // 7. MOSTRAR NOTIFICACIÓN
      this.showSuccess(`Producto agregado: ${product.name}`)
      
      // 8. SI NO CUMPLE LA ESTRATEGIA, MOSTRAR ADVERTENCIA
      if (!item.strategy_compliant) {
        this.showStrategyWarning(item)
      }
    },
    
    // Obtener el lote sugerido según la estrategia y la bodega seleccionada
    async getSuggestedLotByStrategy(productId) {
      console.log(`🎯 Obteniendo lote sugerido para producto ${productId} con estrategia ${this.newEgreso.strategy}`)
      console.log(`📍 Bodega seleccionada: ${this.newEgreso.source_warehouse_id}`)
      
      try {
        // Filtrar inventario por producto y bodega seleccionada
        let inventoryItems = this.inventoryData.filter(item => {
          const matchesProduct = item.product.id === productId
          const matchesWarehouse = item.location?.warehouse?.id === this.newEgreso.source_warehouse_id
          return matchesProduct && matchesWarehouse && item.quantity > 0
        })
        
        console.log(`📦 Items encontrados en bodega: ${inventoryItems.length}`)
        
        if (inventoryItems.length === 0) {
          console.log(`❌ No hay inventario disponible en la bodega seleccionada`)
          return null
        }
        
        let selectedItem = null
        
        // Aplicar estrategia
        if (this.newEgreso.strategy === 'fifo') {
          // FIFO: Primero el más antiguo (fecha de producción más temprana)
          inventoryItems.sort((a, b) => {
            const dateA = a.production_date ? new Date(a.production_date) : new Date(0)
            const dateB = b.production_date ? new Date(b.production_date) : new Date(0)
            return dateA - dateB
          })
          selectedItem = inventoryItems[0]
          console.log(`📅 FIFO: Seleccionado lote más antiguo (producción: ${selectedItem.production_date})`)
          
        } else if (this.newEgreso.strategy === 'lifo') {
          // LIFO: Último en entrar (fecha de producción más reciente)
          inventoryItems.sort((a, b) => {
            const dateA = a.production_date ? new Date(a.production_date) : new Date()
            const dateB = b.production_date ? new Date(b.production_date) : new Date()
            return dateB - dateA
          })
          selectedItem = inventoryItems[0]
          console.log(`📅 LIFO: Seleccionado lote más reciente (producción: ${selectedItem.production_date})`)
          
        } else if (this.newEgreso.strategy === 'fefo') {
          // FEFO: Primero el que vence antes
          inventoryItems = inventoryItems.filter(item => item.expiry_date) // Solo items con fecha de vencimiento
          if (inventoryItems.length === 0) {
            console.log(`⚠️ FEFO: No hay items con fecha de vencimiento`)
            return null
          }
          inventoryItems.sort((a, b) => {
            const dateA = new Date(a.expiry_date)
            const dateB = new Date(b.expiry_date)
            return dateA - dateB
          })
          selectedItem = inventoryItems[0]
          console.log(`📅 FEFO: Seleccionado lote que vence primero (vencimiento: ${selectedItem.expiry_date})`)
        }
        
        if (!selectedItem) {
          return null
        }
        
        // Retornar información del lote sugerido
        return {
          lot_number: selectedItem.lot_number,
          expiry_date: selectedItem.expiry_date,
          production_date: selectedItem.production_date,
          zone: selectedItem.location?.zone?.name,
          quantity: selectedItem.quantity,
          inventory_item_id: selectedItem.id
        }
        
      } catch (error) {
        console.error(`❌ Error obteniendo lote sugerido:`, error)
        return null
      }
    },
    
    // Validar si el producto tiene inventario disponible
    async validateProductInventory(productId, variableData = null) {
      console.log(`🔍 Validando inventario para producto ID: ${productId}`)
      console.log(`📋 Variable data para validación:`, variableData)
      console.log(`📍 Bodega seleccionada: ${this.newEgreso.source_warehouse_id}`)
      
      try {
        // Buscar en inventario local primero - FILTRADO POR BODEGA
        let inventoryItems = this.inventoryData.filter(item => item.product.id === productId)
        
        console.log(`🔍 Items encontrados para producto ${productId}:`, inventoryItems.length)
        console.log(`📋 Todos los items antes de filtrar por bodega:`, inventoryItems.map(i => ({
          id: i.id,
          lot: i.lot_number,
          expiry: i.expiry_date,
          quantity: i.quantity,
          warehouse_id: i.location?.warehouse?.id,
          warehouse_name: i.location?.warehouse?.name
        })))
        
        // Filtrar por bodega seleccionada
        if (this.newEgreso.source_warehouse_id) {
          console.log(`🔍 Bodega seleccionada ID: ${this.newEgreso.source_warehouse_id}`)
          
          inventoryItems = inventoryItems.filter(item => {
            const warehouseId = item.location?.warehouse?.id
            const match = warehouseId === this.newEgreso.source_warehouse_id
            
            console.log(`🔍 Item ${item.id}: warehouse.id=${warehouseId}, matches: ${match}, zone: ${item.location?.zone?.name}`)
            
            return match
          })
          
          console.log(`🔍 Inventario filtrado por bodega ${this.newEgreso.source_warehouse_id}: ${inventoryItems.length} items`)
          console.log(`📋 Items después de filtrar por bodega:`, inventoryItems.map(i => ({
            id: i.id,
            lot: i.lot_number,
            expiry: i.expiry_date,
            quantity: i.quantity
          })))
        }
        
        if (inventoryItems.length === 0) {
          console.log(`❌ No se encontró inventario en la bodega seleccionada para producto ${productId}`)
          
          // Verificar si hay stock en OTRAS bodegas
          const allInventoryItems = this.inventoryData.filter(item => item.product.id === productId && item.quantity > 0)
          if (allInventoryItems.length > 0) {
            // Agrupar por bodega y zona
            const locationsByWarehouse = {}
            
            allInventoryItems.forEach(item => {
              const warehouseName = item.location?.warehouse?.name || 'Bodega desconocida'
              const zoneName = item.location?.zone?.name || 'Zona desconocida'
              
              if (!locationsByWarehouse[warehouseName]) {
                locationsByWarehouse[warehouseName] = []
              }
              
              locationsByWarehouse[warehouseName].push({
                zone: zoneName,
                quantity: item.quantity
              })
            })
            
            console.log(`ℹ️ Stock disponible en otras ubicaciones:`, locationsByWarehouse)
            
            // Crear mensaje informativo detallado
            let infoMessage = `No hay stock en la bodega seleccionada. Stock disponible en:\n`
            Object.keys(locationsByWarehouse).forEach(warehouse => {
              const zones = locationsByWarehouse[warehouse]
              const warehouseTotal = zones.reduce((sum, z) => sum + z.quantity, 0)
              infoMessage += `• ${warehouse} (${warehouseTotal} unidades total):\n`
              zones.forEach(z => {
                infoMessage += `  - ${z.zone}: ${z.quantity} unidades\n`
              })
            })
            
            return { 
              hasInventory: false, 
              hasAlternatives: false, 
              message: infoMessage, 
              suggestedItem: null,
              otherLocations: locationsByWarehouse
            }
          }
          
          return { hasInventory: false, hasAlternatives: false, message: null, suggestedItem: null }
        }
        
        // Si se escaneó un lote específico, buscar TODOS los items del mismo lote en la misma zona
        // (independientemente de la fecha de vencimiento - caso especial: mismo lote, diferentes fechas)
        if (variableData && variableData.lot_number) {
          console.log(`🔍 Validando items del lote ${variableData.lot_number} (sumando todas las fechas de vencimiento en la misma zona)`)
          
          // Filtrar todos los items del mismo lote (ignorando fecha de vencimiento)
          const scannedLot = (variableData.lot_number || '').toUpperCase().trim()
          const itemsWithSameLot = inventoryItems.filter(item => {
            const itemLot = (item.lot_number || '').toUpperCase().trim()
            return itemLot === scannedLot
          })
          
          console.log(`📦 Items encontrados del mismo lote ${variableData.lot_number}:`, itemsWithSameLot.length)
          itemsWithSameLot.forEach(item => {
            console.log(`   - ID ${item.id}: qty=${item.quantity}, expiry=${item.expiry_date}, zone=${item.location?.zone?.name}`)
          })
          
          if (itemsWithSameLot.length === 0) {
            console.log(`❌ No se encontraron items del lote ${variableData.lot_number} en el inventario`)
            console.log(`📋 Items disponibles:`, inventoryItems.map(i => ({
              lot: i.lot_number,
              expiry: i.expiry_date,
              quantity: i.quantity
            })))
            
            // Buscar alternativas con stock disponible
            const itemsWithStock = inventoryItems.filter(item => item.quantity > 0)
            
            if (itemsWithStock.length > 0) {
              const suggestedItem = await this.suggestAlternativeByStrategy(productId, itemsWithStock, variableData)
              
              return {
                hasInventory: false,
                hasAlternatives: true,
                message: `El lote "${variableData.lot_number}" no tiene stock disponible. Hay ${itemsWithStock.length} lote(s) disponible(s).`,
                suggestedItem: suggestedItem,
                inventoryItems: [] // No retornar items porque no hay del lote especificado
              }
            }
            
            return { hasInventory: false, hasAlternatives: false, message: null, suggestedItem: null, inventoryItems: [] }
          }
          
          // Calcular stock total del mismo lote (sumando todas las fechas de vencimiento)
          const totalStockSameLot = itemsWithSameLot.reduce((sum, item) => sum + (item.quantity || 0), 0)
          
          console.log(`✅ Stock total del lote ${variableData.lot_number}: ${totalStockSameLot} unidades (sumando todas las fechas de vencimiento)`)
          
          if (totalStockSameLot <= 0) {
            console.log(`❌ El lote "${variableData.lot_number}" no tiene stock disponible`)
            
            // Buscar alternativas con stock disponible
            const itemsWithStock = inventoryItems.filter(item => item.quantity > 0)
            
            if (itemsWithStock.length > 0) {
              const suggestedItem = await this.suggestAlternativeByStrategy(productId, itemsWithStock, variableData)
              
              let message = `El lote "${variableData.lot_number}" no tiene stock disponible. `
              if (suggestedItem) {
                if (this.newEgreso.strategy === 'libre') {
                  message += `Hay ${itemsWithStock.length} lote(s) disponible(s).`
                } else {
                  message += `Según estrategia ${this.newEgreso.strategy.toUpperCase()}, se sugiere otro lote.`
                }
                
                return {
                  hasInventory: false,
                  hasAlternatives: true,
                  message: message,
                  suggestedItem: suggestedItem,
                  inventoryItems: []
                }
              }
            }
            
            return { hasInventory: false, hasAlternatives: false, message: null, suggestedItem: null, inventoryItems: [] }
          }
          
          // Retornar TODOS los items del mismo lote (con todas las fechas de vencimiento)
          console.log(`✅ Retornando ${itemsWithSameLot.length} items del mismo lote con stock total: ${totalStockSameLot} unidades`)
          return { 
            hasInventory: true, 
            hasAlternatives: false, 
            message: null, 
            suggestedItem: null,
            inventoryItems: itemsWithSameLot // Retornar TODOS los items del mismo lote
          }
        }
        
        // Si solo hay fecha de vencimiento (sin lote), buscar items con esa fecha
        if (variableData && variableData.expiry_date && !variableData.lot_number) {
          const scannedExpiry = variableData.expiry_date ? variableData.expiry_date.substring(0, 10) : null
          const itemsWithSameExpiry = inventoryItems.filter(item => {
            const itemExpiry = item.expiry_date ? item.expiry_date.substring(0, 10) : null
            return itemExpiry === scannedExpiry
          })
          
          const totalStockSameExpiry = itemsWithSameExpiry.reduce((sum, item) => sum + (item.quantity || 0), 0)
          
          if (totalStockSameExpiry <= 0) {
            return { hasInventory: false, hasAlternatives: false, message: null, suggestedItem: null, inventoryItems: [] }
          }
          
          return {
            hasInventory: true,
            hasAlternatives: false,
            message: null,
            suggestedItem: null,
            inventoryItems: itemsWithSameExpiry
          }
        }
        
        // Si no hay lote específico, verificar stock total
        const totalQuantity = inventoryItems.reduce((sum, item) => sum + item.quantity, 0)
        
        if (totalQuantity <= 0) {
          console.log(`❌ Inventario sin cantidad disponible para producto ${productId}: ${totalQuantity}`)
          return { hasInventory: false, hasAlternatives: false, message: null, suggestedItem: null, inventoryItems: [] }
        }
        
        console.log(`✅ Inventario disponible para producto ${productId}: ${totalQuantity} unidades`)
        return { 
          hasInventory: true, 
          hasAlternatives: false, 
          message: null, 
          suggestedItem: null,
          inventoryItems: inventoryItems.filter(item => item.quantity > 0) // Retornar items con stock
        }
        
      } catch (error) {
        console.error(`❌ Error validando inventario para producto ${productId}:`, error)
        return { hasInventory: false, hasAlternatives: false, message: null, suggestedItem: null }
      }
    },
    
    // Sugerir lote alternativo según la estrategia seleccionada
    async suggestAlternativeByStrategy(productId, itemsWithStock, scannedVariableData) {
      console.log(`🎯 Sugiriendo alternativa según estrategia: ${this.newEgreso.strategy}`)
      console.log(`📦 Items con stock para sugerir:`, itemsWithStock.length)
      console.log(`📋 Variable data escaneada:`, scannedVariableData)
      
      try {
        // Obtener información detallada de items disponibles
        const response = await api.get(`/inventory/products/${productId}/available-items`)
        const availableData = response.data
        
        console.log(`📊 Available data del endpoint:`, availableData)
        
        if (this.newEgreso.strategy === 'libre') {
          // Estrategia libre: sugerir cualquier lote disponible (el primero)
          const firstAvailable = itemsWithStock[0]
          console.log(`📦 Estrategia LIBRE: Sugiriendo primer lote disponible:`, firstAvailable)
          
          return {
            type: scannedVariableData.lot_number ? 'lot' : 'expiry_date',
            value: scannedVariableData.lot_number ? firstAvailable.lot_number : firstAvailable.expiry_date,
            available_quantity: firstAvailable.quantity,
            date: firstAvailable.expiry_date || firstAvailable.created_at
          }
        } else if (this.newEgreso.strategy === 'fifo') {
          // FIFO: sugerir el lote más antiguo
          const lots = availableData.lots || []
          const sortedLots = [...lots].sort((a, b) => new Date(a.entry_date) - new Date(b.entry_date))
          const oldestWithStock = sortedLots.find(lot => lot.quantity > 0)
          
          if (oldestWithStock) {
            console.log(`📦 Estrategia FIFO: Sugiriendo lote más antiguo:`, oldestWithStock)
            return {
              type: 'lot',
              value: oldestWithStock.value,
              available_quantity: oldestWithStock.quantity,
              date: oldestWithStock.entry_date
            }
          }
        } else if (this.newEgreso.strategy === 'fefo') {
          // FEFO: sugerir el lote que vence primero
          const expiryDates = availableData.expiry_dates || []
          const sortedExpiry = [...expiryDates].sort((a, b) => 
            new Date(a.expiry_date || a.value) - new Date(b.expiry_date || b.value)
          )
          const earliestWithStock = sortedExpiry.find(exp => exp.quantity > 0)
          
          if (earliestWithStock) {
            console.log(`📦 Estrategia FEFO: Sugiriendo lote con vencimiento más próximo:`, earliestWithStock)
            return {
              type: 'expiry_date',
              value: earliestWithStock.expiry_date || earliestWithStock.value,
              available_quantity: earliestWithStock.quantity
            }
          }
        } else if (this.newEgreso.strategy === 'lifo') {
          // LIFO: sugerir el lote más reciente
          const lots = availableData.lots || []
          const sortedLots = [...lots].sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date))
          const newestWithStock = sortedLots.find(lot => lot.quantity > 0)
          
          if (newestWithStock) {
            console.log(`📦 Estrategia LIFO: Sugiriendo lote más reciente:`, newestWithStock)
            return {
              type: 'lot',
              value: newestWithStock.value,
              available_quantity: newestWithStock.quantity,
              date: newestWithStock.entry_date
            }
          }
        }
        
        // Fallback: retornar el primero disponible
        const firstAvailable = itemsWithStock[0]
        return {
          type: scannedVariableData.lot_number ? 'lot' : 'expiry_date',
          value: scannedVariableData.lot_number ? firstAvailable.lot_number : firstAvailable.expiry_date,
          available_quantity: firstAvailable.quantity,
          date: firstAvailable.expiry_date || firstAvailable.created_at
        }
        
      } catch (error) {
        console.error('❌ Error sugiriendo alternativa:', error)
        // Fallback simple
        const firstAvailable = itemsWithStock[0]
        return {
          type: scannedVariableData.lot_number ? 'lot' : 'expiry_date',
          value: scannedVariableData.lot_number ? firstAvailable.lot_number : firstAvailable.expiry_date,
          available_quantity: firstAvailable.quantity
        }
      }
    },
    
    // Buscar item duplicado basado en SKU y código completo
    // Nueva función simplificada para detectar duplicados por código original
    findDuplicateByOriginalCode(sku, originalCode) {
      console.log(`🔍 [DUPLICATE] Buscando duplicados por código original`)
      console.log(`🔍 [DUPLICATE] SKU buscado: ${sku}`)
      console.log(`🔍 [DUPLICATE] Código original buscado: ${originalCode}`)
      console.log(`🔍 [DUPLICATE] Total items en lista: ${this.newEgreso.items.length}`)
      
      for (let i = 0; i < this.newEgreso.items.length; i++) {
        const item = this.newEgreso.items[i]
        
        // Comparar SKU y código escaneado original
        // Usamos original_scanned_code si existe, sino barcode_scanned
        const itemOriginalCode = item.original_scanned_code || item.barcode_scanned
        
        console.log(`🔍 [DUPLICATE] Item ${i}:`, {
          sku: item.product_sku,
          original_scanned_code: item.original_scanned_code,
          barcode_scanned: item.barcode_scanned,
          itemOriginalCode: itemOriginalCode,
          lot_number: item.lot_number,
          quantity: item.quantity,
          sku_match: item.product_sku === sku,
          code_match: itemOriginalCode === originalCode
        })
        
        if (item.product_sku === sku && itemOriginalCode === originalCode) {
          console.log(`♻️ [DUPLICATE] ¡DUPLICADO ENCONTRADO en índice ${i}!`)
          return i
        }
      }
      
      console.log(`✅ [DUPLICATE] No se encontraron duplicados`)
      return -1
    },
    
    // Función anterior mantenida para compatibilidad (por si se usa en otro lugar)
    findDuplicateItem(sku, originalCode, variableData) {
      console.log(`🔍 Buscando duplicados para SKU: ${sku}, Código: ${originalCode}`)
      
      for (let i = 0; i < this.newEgreso.items.length; i++) {
        const item = this.newEgreso.items[i]
        
        // Comparar SKU y código de barras exacto
        if (item.product_sku === sku && item.barcode_scanned === originalCode) {
          // Para códigos GS1, también verificar que coincidan todos los AIs
          const sameVariableData = this.compareVariableData(item, variableData)
          
          if (sameVariableData) {
            console.log(`♻️ Duplicado encontrado en índice ${i}:`, {
              sku: item.product_sku,
              código: item.barcode_scanned,
              cantidad_actual: item.quantity
            })
            return i
          }
        }
      }
      
      console.log(`✅ No se encontraron duplicados para SKU: ${sku}`)
      return -1
    },
    
    // Comparar datos variables para determinar si son el mismo producto exacto
    compareVariableData(existingItem, newVariableData) {
      // Comparar campos clave que determinan si es el mismo producto exacto
      const fieldsToCompare = ['lot_number', 'expiry_date', 'production_date', 'serial_number']
      
      for (const field of fieldsToCompare) {
        const existingValue = existingItem[field] || null
        const newValue = newVariableData[field] || null
        
        // Si ambos valores existen y son diferentes, no es duplicado
        if (existingValue && newValue && existingValue !== newValue) {
          console.log(`🔍 Diferencia en ${field}: "${existingValue}" vs "${newValue}"`)
          return false
        }
        
        // Si solo uno tiene valor, no es duplicado
        if ((existingValue && !newValue) || (!existingValue && newValue)) {
          console.log(`🔍 Diferencia en ${field}: uno tiene valor y el otro no`)
          return false
        }
      }
      
      console.log(`✅ Los datos variables coinciden - es el mismo producto exacto`)
      return true
    },

    addProductManually() {
      // TODO: Implementar modal para agregar producto manualmente
      this.showInfo('Funcionalidad pendiente de implementar')
    },
    
    removeProduct(index) {
      this.newEgreso.items.splice(index, 1)
    },
    
    // Advertencias de estrategia
    showStrategyWarning(item) {
      this.strategyWarningMessage = item.strategy_warning
      this.suggestedProduct = item.suggested_product
      this.currentValidatingItem = item // Guardar referencia del item que está siendo validado
      this.showStrategyWarningDialog = true
    },
    
    closeStrategyWarningDialog() {
      this.showStrategyWarningDialog = false
      this.strategyWarningMessage = ''
      this.suggestedProduct = null
      this.currentValidatingItem = null
    },
    
    ignoreStrategyWarning() {
      console.log('🚫 [STRATEGY] Usuario ignora advertencia')
      
      // Verificar si el item ya está en la lista
      const existingIndex = this.newEgreso.items.findIndex(i => 
        i.product_id === this.currentValidatingItem.product_id && 
        i.barcode_scanned === this.currentValidatingItem.barcode_scanned
      )
      
      if (existingIndex >= 0) {
        // El item ya está en la lista (es una advertencia de estrategia normal)
        // Mantener el producto original sin cambios (ya está en la tabla)
        this.currentValidatingItem.strategy_compliant = false // Marcar como no cumple pero permitir
        this.newEgreso.items[existingIndex].strategy_compliant = false
        this.closeStrategyWarningDialog()
        this.showInfo('Estrategia ignorada. El producto se mantendrá tal como fue escaneado.')
      } else {
        // Es un caso de lote sin stock - no se puede ignorar
        this.closeStrategyWarningDialog()
        this.showError('No se puede agregar un producto sin stock. Debe usar el lote sugerido o escanear otro producto.')
      }
      
      // Volver a enfocar el input para continuar escaneando
      this.focusScannerInput()
    },
    
    async acceptSuggestedProduct() {
      console.log('✅ [STRATEGY] Usuario acepta producto sugerido')
      
      if (!this.currentValidatingItem || !this.suggestedProduct) {
        this.showError('Error: No se encontró información del producto sugerido')
        return
      }
      
      const item = this.currentValidatingItem
      const suggested = this.suggestedProduct
      
      console.log('🔄 [STRATEGY] Aplicando sugerencia:', suggested)
      console.log('🔍 [STRATEGY] Item actual:', {
        product_id: item.product_id,
        barcode_scanned: item.barcode_scanned,
        original_scanned_code: item.original_scanned_code,
        lot_number: item.lot_number
      })
      
      // Verificar si el item ya está en la lista (validación de estrategia después de agregar)
      // o si es un item temporal (sin stock en lote escaneado)
      // Buscar por original_scanned_code para detectar correctamente items corregidos
      const existingIndex = this.newEgreso.items.findIndex(i => {
        const iOriginalCode = i.original_scanned_code || i.barcode_scanned
        const itemOriginalCode = item.original_scanned_code || item.barcode_scanned
        return i.product_id === item.product_id && iOriginalCode === itemOriginalCode
      })
      
      console.log('🔍 [STRATEGY] Index de item existente:', existingIndex)
      
      if (suggested.type === 'expiry_date') {
        // Actualizar fecha de vencimiento - MANTENER FORMATO COMPLETO CON HORA
        item.expiry_date = suggested.value // Mantener formato completo: 2027-01-30T00:00:00
        console.log(`📅 [STRATEGY] Fecha de vencimiento actualizada: ${item.expiry_date}`)
        
        // CRÍTICO: También actualizar el lot_number correspondiente a esta fecha de vencimiento
        console.log(`🔍 [STRATEGY] Buscando lote correspondiente a fecha ${suggested.value}`)
        await this.findCorrectLotForExpiryDate(item, suggested.value)
      } else if (suggested.type === 'lot') {
        // Actualizar lote
        const oldLotNumber = item.lot_number
        item.lot_number = suggested.value
        console.log(`🏷️ [STRATEGY] Lote actualizado: ${oldLotNumber} → ${item.lot_number}`)
        
        // CRÍTICO: También actualizar expiry_date correspondiente al nuevo lote
        console.log(`🔍 [STRATEGY] Buscando fecha de vencimiento correspondiente al lote ${suggested.value}`)
        await this.findCorrectExpiryDateForLot(item, suggested.value)
      }
      
      // Marcar como cumpliendo la estrategia
      item.strategy_compliant = true
      item.strategy_warning = null
      item.suggested_product = null
      
      // Asegurar que original_scanned_code esté preservado
      if (!item.original_scanned_code) {
        item.original_scanned_code = item.barcode_scanned
        console.log('🔧 [STRATEGY] Estableciendo original_scanned_code:', item.original_scanned_code)
      }
      
      // Si el item ya existe en la lista, actualizarlo; sino, agregarlo
      if (existingIndex >= 0) {
        // Actualizar item existente - preservar original_scanned_code del existente
        const existingItem = this.newEgreso.items[existingIndex]
        this.newEgreso.items[existingIndex] = { 
          ...item,
          original_scanned_code: existingItem.original_scanned_code || item.original_scanned_code
        }
        console.log('🔄 [STRATEGY] Item existente actualizado en la lista')
        console.log('🔍 [STRATEGY] Item actualizado:', this.newEgreso.items[existingIndex])
      } else {
        // Agregar nuevo item a la lista
        this.newEgreso.items.push(item)
        console.log('➕ [STRATEGY] Nuevo item agregado a la lista')
        console.log('🔍 [STRATEGY] Item agregado:', item)
      }
      
      this.closeStrategyWarningDialog()
      this.showSuccess('Producto agregado/actualizado con lote sugerido.')
      
      // Volver a enfocar el input para continuar escaneando
      this.focusScannerInput()
    },
    
    // Función para encontrar el lot_number correcto basado en expiry_date
    async findCorrectLotForExpiryDate(item, targetExpiryDate) {
      try {
        console.log(`🔍 [LOT_FINDER] Buscando lote para expiry_date: ${targetExpiryDate} en producto ${item.product_id}`)
        
        // Hacer llamada al endpoint available-items para obtener información detallada
        const response = await api.get(`/inventory/products/${item.product_id}/available-items`)
        const availableData = response.data
        
        console.log(`📊 [LOT_FINDER] Available data items:`, availableData.items)
        
        // Usar la información detallada de items para mapeo exacto
        if (availableData.items) {
          console.log(`🔍 [LOT_FINDER] Searching in ${availableData.items.length} detailed items`)
          console.log(`🎯 [LOT_FINDER] Target expiry_date: "${targetExpiryDate}"`)
          
          // Normalizar la fecha objetivo para comparación
          const normalizeDate = (dateStr) => {
            if (!dateStr) return null
            
            // Convertir a Date object y luego a formato ISO para comparar solo la fecha
            const date = new Date(dateStr)
            if (isNaN(date.getTime())) return null
            
            // Retornar solo la parte de fecha en formato YYYY-MM-DD
            return date.toISOString().substring(0, 10)
          }
          
          const targetDateNormalized = normalizeDate(targetExpiryDate)
          console.log(`🎯 [LOT_FINDER] Target date normalized: "${targetDateNormalized}"`)
          
          // Buscar el item que tenga exactamente la fecha de vencimiento objetivo
          const matchingItem = availableData.items.find(detailItem => {
            if (!detailItem.expiry_date) return false
            
            const detailDateNormalized = normalizeDate(detailItem.expiry_date)
            
            console.log(`🔄 [LOT_FINDER] Comparing item:`)
            console.log(`   - lot_number: "${detailItem.lot_number}"`)
            console.log(`   - expiry_date: "${detailItem.expiry_date}"`)
            console.log(`   - normalized: "${detailDateNormalized}"`)
            console.log(`   - match? ${detailDateNormalized === targetDateNormalized}`)
            
            return detailDateNormalized === targetDateNormalized
          })
          
          if (matchingItem) {
            console.log(`✅ [LOT_FINDER] Encontrado mapeo exacto:`)
            console.log(`   - lot_number: "${matchingItem.lot_number}"`)
            console.log(`   - expiry_date: "${matchingItem.expiry_date}"`)
            
            // Actualizar tanto lote como fecha para asegurar consistencia
            const oldLotNumber = item.lot_number
            const oldExpiryDate = item.expiry_date
            
            item.lot_number = matchingItem.lot_number
            
            // Mantener formato ISO para consistency
            const normalizedTargetDate = new Date(targetExpiryDate).toISOString()
            item.expiry_date = normalizedTargetDate
            
            console.log(`🔄 [LOT_FINDER] CAMBIOS APLICADOS:`)
            console.log(`   - lot_number: "${oldLotNumber}" → "${matchingItem.lot_number}"`)
            console.log(`   - expiry_date: "${oldExpiryDate}" → "${normalizedTargetDate}"`)
          } else {
            console.log(`❌ [LOT_FINDER] No se encontró item con expiry_date: ${targetExpiryDate}`)
            console.log(`🔍 [LOT_FINDER] Available items:`)
            availableData.items.forEach((item, i) => {
              console.log(`   ${i + 1}. lot: "${item.lot_number}", expiry: "${item.expiry_date}", normalized: "${normalizeDate(item.expiry_date)}"`)
            })
          }
        } else {
          console.log(`❌ [LOT_FINDER] No hay información detallada de items en la respuesta`)
        }
        
      } catch (error) {
        console.error('❌ [LOT_FINDER] Error buscando lote correcto:', error)
      }
    },
    
    // Función para encontrar el expiry_date correcto basado en lot_number  
    async findCorrectExpiryDateForLot(item, targetLotNumber) {
      try {
        console.log(`🔍 [EXPIRY_FINDER] Buscando fecha de vencimiento para lote: ${targetLotNumber} en producto ${item.product_id}`)
        
        // Hacer llamada al endpoint available-items para obtener información detallada
        const response = await api.get(`/inventory/products/${item.product_id}/available-items`)
        const availableData = response.data
        
        console.log(`📊 [EXPIRY_FINDER] Available data items:`, availableData.items)
        
        // Usar la información detallada de items para mapeo exacto
        if (availableData.items) {
          console.log(`🔍 [EXPIRY_FINDER] Searching in ${availableData.items.length} detailed items`)
          console.log(`🎯 [EXPIRY_FINDER] Target lot_number: "${targetLotNumber}"`)
          
          // Buscar el item que tenga exactamente el lote objetivo
          const matchingItem = availableData.items.find(detailItem => {
            return detailItem.lot_number === targetLotNumber
          })
          
          if (matchingItem) {
            console.log(`✅ [EXPIRY_FINDER] Encontrado mapeo exacto:`)
            console.log(`   - lot_number: "${matchingItem.lot_number}"`)
            console.log(`   - expiry_date: "${matchingItem.expiry_date}"`)
            
            // Actualizar fecha de vencimiento para consistencia
            const oldExpiryDate = item.expiry_date
            
            // Mantener formato ISO para consistency
            if (matchingItem.expiry_date) {
              const normalizedExpiryDate = new Date(matchingItem.expiry_date).toISOString()
              item.expiry_date = normalizedExpiryDate
            } else {
              item.expiry_date = null
            }
            
            console.log(`🔄 [EXPIRY_FINDER] CAMBIOS APLICADOS:`)
            console.log(`   - expiry_date: "${oldExpiryDate}" → "${item.expiry_date}"`)
          } else {
            console.log(`❌ [EXPIRY_FINDER] No se encontró item con lot_number: ${targetLotNumber}`)
            console.log(`🔍 [EXPIRY_FINDER] Available items:`)
            availableData.items.forEach((item, i) => {
              console.log(`   ${i + 1}. lot: "${item.lot_number}", expiry: "${item.expiry_date}"`)
            })
          }
        } else {
          console.log(`❌ [EXPIRY_FINDER] No hay información detallada de items en la respuesta`)
        }
        
      } catch (error) {
        console.error('❌ [EXPIRY_FINDER] Error buscando fecha de vencimiento correcta:', error)
      }
    },
    
    // CRUD operations
    async saveEgreso() {
      if (!this.canSaveEgreso) return
      
      try {
        this.submitting = true
        
        // Validar que se haya seleccionado ubicación
        if (!this.newEgreso.location_id) {
          this.showError('Debe seleccionar una bodega para determinar la ubicación de origen')
          return
        }
        
        const data = {
          customer_id: this.newEgreso.customer_id,
          strategy: this.newEgreso.strategy,
          location_id: this.newEgreso.location_id,  // Zone ID desde donde se realiza el egreso
          notes: this.newEgreso.notes,
          items: this.newEgreso.items
        }
        
        console.log('📤 [SAVE EGRESO] Sending data to backend:')
        console.log('🏢 Customer ID:', data.customer_id)
        console.log('📋 Strategy:', data.strategy)
        console.log('📍 Location ID (Zone ID):', data.location_id)
        console.log('📝 Notes:', data.notes)
        console.log('📦 Items:', data.items)
        data.items.forEach((item, index) => {
          console.log(`   Item ${index + 1}:`, {
            product_id: item.product_id,
            sku: item.sku,
            quantity: item.quantity,
            lot_number: item.lot_number,
            expiry_date: item.expiry_date,
            strategy_compliant: item.strategy_compliant
          })
        })
        
        const response = await api.post('/egresos/egress-orders', data)
        
        if (response.data) {
          this.showSuccess('Orden de egreso creada exitosamente')
          this.closeCreateModal()
          await this.loadEgresos()
        }
        
      } catch (error) {
        console.error('Error saving egreso:', error)
        const errorMessage = error.response?.data?.error || 'Error al crear orden de egreso'
        this.showError(errorMessage)
      } finally {
        this.submitting = false
      }
    },
    
    viewEgreso(egreso) {
      // TODO: Implementar modal de visualización
      console.log('View egreso:', egreso)
      this.showInfo('Vista de detalle pendiente de implementar')
    },
    
    viewEgreso(egreso) {
      this.selectedEgreso = egreso
      this.showReportModal = true
    },
    
    printReport() {
      // Crear una ventana nueva para imprimir
      const printWindow = window.open('', '_blank')
      const reportContent = this.generatePrintableReport()
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Reporte de Salida - ${this.selectedEgreso.order_number}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .info-section { margin-bottom: 20px; }
            .info-section h3 { margin-bottom: 10px; border-bottom: 2px solid #333; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .summary { margin-top: 20px; }
            .summary-item { display: flex; justify-content: space-between; margin-bottom: 5px; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          ${reportContent}
        </body>
        </html>
      `)
      
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
      printWindow.close()
    },
    
    generatePrintableReport() {
      if (!this.selectedEgreso) return ''
      
      const items = this.selectedEgreso.items || []
      const totalSKUs = items.length
      const totalUnits = items.reduce((sum, item) => sum + (item.quantity || 0), 0)
      
      return `
        <div class="header">
          <h1>REPORTE DE SALIDA</h1>
          <h2>Orden: ${this.selectedEgreso.order_number}</h2>
        </div>
        
        <div class="info-section">
          <h3>Información General</h3>
          <p><strong>Fecha:</strong> ${this.formatDate(this.selectedEgreso.created_at)}</p>
          <p><strong>Cliente:</strong> ${this.selectedEgreso.customer?.name || 'No especificado'}</p>
          <p><strong>RUT:</strong> ${this.selectedEgreso.customer?.tax_id || '-'}</p>
          <p><strong>Estrategia:</strong> ${this.getStrategyText(this.selectedEgreso.strategy)}</p>
          <p><strong>Usuario:</strong> ${this.selectedEgreso.user || 'Sistema'}</p>
          <p><strong>Notas:</strong> ${this.selectedEgreso.notes || 'Sin notas'}</p>
        </div>
        
        <div class="info-section">
          <h3>Productos</h3>
          <table>
            <thead>
              <tr>
                <th>Producto</th>
                <th>SKU</th>
                <th>Cantidad</th>
                <th>Lote</th>
                <th>Vencimiento</th>
                <th>S/N</th>
                <th>F. Fabricación</th>
              </tr>
            </thead>
            <tbody>
              ${items.map(item => `
                <tr>
                  <td>${item.product?.name || 'Producto desconocido'}</td>
                  <td>${item.product?.sku || '-'}</td>
                  <td>${item.quantity}</td>
                  <td>${item.lot_number || '-'}</td>
                  <td>${this.formatDate(item.expiry_date) || '-'}</td>
                  <td>${item.serial_number || '-'}</td>
                  <td>${this.formatDate(item.production_date) || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        
        <div class="summary">
          <h3>Resumen</h3>
          <div class="summary-item">
            <strong>Total de SKUs:</strong>
            <span>${totalSKUs}</span>
          </div>
          <div class="summary-item">
            <strong>Total de Unidades:</strong>
            <span>${totalUnits}</span>
          </div>
        </div>
      `
    },
    
    async deleteEgreso(egreso) {
      if (!confirm(`¿Está seguro de eliminar la orden ${egreso.order_number}?`)) {
        return
      }
      
      try {
        await api.delete(`/egresos/egress-orders/${egreso.id}`)
        this.showSuccess('Orden eliminada exitosamente')
        await this.loadEgresos()
      } catch (error) {
        console.error('Error deleting egreso:', error)
        this.showError('Error al eliminar orden')
      }
    },
    
    // Export to CSV
    async exportEgresosToCSV() {
      try {
        this.exportingCSV = true
        console.log('📊 Exportando egresos filtrados a CSV...')
        
        // Usar los egresos ya filtrados
        const egresosToExport = this.filteredEgresos
        
        if (egresosToExport.length === 0) {
          this.showError('No hay órdenes para exportar con los filtros actuales')
          return
        }
        
        console.log(`📦 Exportando ${egresosToExport.length} órdenes`)
        
        // Crear array de filas expandidas (una fila por producto)
        const rows = []
        
        // Encabezados
        const headers = [
          'Número de Orden',
          'Fecha',
          'Cliente',
          'RUT',
          'Estrategia',
          'Usuario',
          'Notas',
          'SKU Producto',
          'Nombre Producto',
          'Cantidad',
          'Lote',
          'Fecha Vencimiento',
          'Número de Serie',
          'Fecha Fabricación'
        ]
        
        rows.push(headers)
        
        // Procesar cada orden
        for (const egreso of egresosToExport) {
          const orderInfo = {
            orderNumber: egreso.order_number || '-',
            date: this.formatDate(egreso.created_at),
            customer: egreso.customer?.name || 'No especificado',
            taxId: egreso.customer?.tax_id || '-',
            strategy: this.getStrategyText(egreso.strategy),
            user: egreso.user || 'Sistema',
            notes: egreso.notes || 'Sin notas'
          }
          
          // Si la orden tiene productos, crear una fila por producto
          if (egreso.items && egreso.items.length > 0) {
            for (const item of egreso.items) {
              const row = [
                orderInfo.orderNumber,
                orderInfo.date,
                orderInfo.customer,
                orderInfo.taxId,
                orderInfo.strategy,
                orderInfo.user,
                orderInfo.notes,
                item.product?.sku || '-',
                item.product?.name || 'Producto desconocido',
                item.quantity || 0,
                item.lot_number || '-',
                this.formatDate(item.expiry_date) || '-',
                item.serial_number || '-',
                this.formatDate(item.production_date) || '-'
              ]
              rows.push(row)
            }
          } else {
            // Si no tiene productos, crear una fila con la info de la orden
            const row = [
              orderInfo.orderNumber,
              orderInfo.date,
              orderInfo.customer,
              orderInfo.taxId,
              orderInfo.strategy,
              orderInfo.user,
              orderInfo.notes,
              '-', // SKU
              'Sin productos', // Nombre
              0, // Cantidad
              '-', // Lote
              '-', // Vencimiento
              '-', // Serial
              '-'  // Fabricación
            ]
            rows.push(row)
          }
        }
        
        console.log(`✅ ${rows.length - 1} filas generadas (incluyendo productos)`)
        
        // Convertir a CSV
        const csvContent = rows.map(row => 
          row.map(cell => {
            // Escapar comillas y comas en el contenido
            const cellStr = String(cell).replace(/"/g, '""')
            return `"${cellStr}"`
          }).join(',')
        ).join('\n')
        
        // Agregar BOM para que Excel reconozca UTF-8
        const BOM = '\uFEFF'
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
        
        // Generar nombre de archivo con fecha
        const today = new Date().toISOString().split('T')[0]
        const filename = `egresos_${today}.csv`
        
        // Descargar archivo
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        this.showSuccess(`Archivo ${filename} descargado exitosamente`)
        console.log(`✅ CSV exportado: ${filename}`)
        
      } catch (error) {
        console.error('❌ Error exportando CSV:', error)
        this.showError('Error al exportar el archivo CSV')
      } finally {
        this.exportingCSV = false
      }
    },
    
    // Utility methods
    formatDate(dateString) {
      if (!dateString) return '-'
      try {
        return new Date(dateString).toLocaleDateString('es-ES')
      } catch {
        return dateString
      }
    },
    
    getStrategyColor(strategy) {
      const colors = {
        'libre': 'grey',
        'fifo': 'blue',
        'fefo': 'orange',
        'lifo': 'green'
      }
      return colors[strategy] || 'grey'
    },
    
    getStrategyText(strategy) {
      const texts = {
        'libre': 'Libre',
        'fifo': 'FIFO',
        'fefo': 'FEFO',
        'lifo': 'LIFO'
      }
      return texts[strategy] || strategy
    },
    

    
    // Notificaciones
    showSuccess(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'success',
        timeout: 4000
      }
    },
    
    showError(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'error',
        timeout: 6000
      }
    },
    
    showInfo(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'info',
        timeout: 4000
      }
    },
    
    // Métodos de validación de estrategias
    validateFIFOStrategy(item, availableItems) {
      console.log(`🔍 [FIFO] Validating item:`, item)
      console.log(`📦 [FIFO] Available items:`, availableItems)
      
      if (!item.lot_number) {
        console.log(`❌ [FIFO] No lot_number on item`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      const lots = availableItems.lots || []
      if (lots.length === 0) {
        console.log(`❌ [FIFO] No lots available`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      console.log(`🏷️ [FIFO] Available lots:`, lots.map(l => `${l.value} (${l.entry_date})`))
      
      // Ordenar por fecha de entrada (más antigua primero)
      const sortedLots = [...lots].sort((a, b) => new Date(a.entry_date) - new Date(b.entry_date))
      console.log(`🗂️ [FIFO] Sorted lots (oldest first):`, sortedLots.map(l => `${l.value} (${l.entry_date})`))
      
      // Encontrar el lote escaneado
      const scannedLot = lots.find(lot => lot.value === item.lot_number)
      if (!scannedLot) {
        console.log(`❌ [FIFO] Scanned lot '${item.lot_number}' not found in available lots`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      console.log(`📦 [FIFO] Scanned lot found:`, scannedLot)
      
      // Verificar si hay lotes más antiguos con stock disponible
      const olderLotsWithStock = sortedLots.filter(lot => 
        new Date(lot.entry_date) < new Date(scannedLot.entry_date) && 
        lot.quantity > 0
      )
      
      console.log(`⏰ [FIFO] Older lots with stock:`, olderLotsWithStock.map(l => `${l.value} (${l.entry_date}, qty: ${l.quantity})`))
      
      if (olderLotsWithStock.length > 0) {
        const oldestLot = olderLotsWithStock[0]
        console.log(`❌ [FIFO] Strategy violation! Suggesting oldest lot:`, oldestLot)
        return {
          compliant: false,
          warning: `FIFO: Hay ${olderLotsWithStock.length} lote(s) con fecha de ingreso más antigua disponible(s). El más antiguo es '${oldestLot.value}' con ${oldestLot.quantity} unidades (ingreso: ${this.formatDate(oldestLot.entry_date)})`,
          suggestedItem: {
            type: 'lot',
            value: oldestLot.value,
            date: oldestLot.entry_date,
            available_quantity: oldestLot.quantity
          }
        }
      }
      
      console.log(`✅ [FIFO] Strategy compliant`)
      return { compliant: true, warning: null, suggestedItem: null }
    },
    
    validateLIFOStrategy(item, availableItems) {
      console.log(`🔍 [LIFO] Validating item:`, item)
      console.log(`📦 [LIFO] Available items:`, availableItems)
      
      if (!item.lot_number) {
        console.log(`❌ [LIFO] No lot_number on item`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      const lots = availableItems.lots || []
      if (lots.length === 0) {
        console.log(`❌ [LIFO] No lots available`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      console.log(`🏷️ [LIFO] Available lots:`, lots.map(l => `${l.value} (${l.entry_date})`))
      
      // Ordenar por fecha de entrada (más reciente primero)
      const sortedLots = [...lots].sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date))
      console.log(`🗂️ [LIFO] Sorted lots (newest first):`, sortedLots.map(l => `${l.value} (${l.entry_date})`))
      
      // Encontrar el lote escaneado
      const scannedLot = lots.find(lot => lot.value === item.lot_number)
      if (!scannedLot) {
        console.log(`❌ [LIFO] Scanned lot '${item.lot_number}' not found in available lots`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      console.log(`📦 [LIFO] Scanned lot found:`, scannedLot)
      
      // Verificar si hay lotes más recientes con stock disponible
      const newerLotsWithStock = sortedLots.filter(lot => 
        new Date(lot.entry_date) > new Date(scannedLot.entry_date) && 
        lot.quantity > 0
      )
      
      console.log(`⏰ [LIFO] Newer lots with stock:`, newerLotsWithStock.map(l => `${l.value} (${l.entry_date}, qty: ${l.quantity})`))
      
      if (newerLotsWithStock.length > 0) {
        const newestLot = newerLotsWithStock[0]
        console.log(`❌ [LIFO] Strategy violation! Suggesting newest lot:`, newestLot)
        return {
          compliant: false,
          warning: `LIFO: Hay ${newerLotsWithStock.length} lote(s) con fecha de ingreso más reciente disponible(s). El más reciente es '${newestLot.value}' con ${newestLot.quantity} unidades (ingreso: ${this.formatDate(newestLot.entry_date)})`,
          suggestedItem: {
            type: 'lot',
            value: newestLot.value,
            date: newestLot.entry_date,
            available_quantity: newestLot.quantity
          }
        }
      }
      
      console.log(`✅ [LIFO] Strategy compliant`)
      return { compliant: true, warning: null, suggestedItem: null }
    },
    
    validateFEFOStrategy(item, availableItems) {
      if (!item.expiry_date) {
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      const expiryDates = availableItems.expiry_dates || []
      if (expiryDates.length === 0) {
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      // Ordenar por fecha de vencimiento (más próxima primero)
      const sortedExpiry = [...expiryDates].sort((a, b) => new Date(a.expiry_date || a.value) - new Date(b.expiry_date || b.value))
      
      // Normalizar fecha del item escaneado para comparación (solo fecha, sin hora)
      const scannedDateStr = item.expiry_date
      const scannedDateOnly = scannedDateStr ? scannedDateStr.substring(0, 10) : ''
      
      // Encontrar la fecha de vencimiento escaneada (comparando solo la parte de fecha)
      const scannedExpiry = expiryDates.find(exp => {
        const expDateStr = exp.expiry_date || exp.value || ''
        const expDateOnly = expDateStr.substring(0, 10)
        return expDateOnly === scannedDateOnly
      })
      
      if (!scannedExpiry) {
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      const scannedDate = new Date(scannedExpiry.expiry_date || scannedExpiry.value)
      
      // Verificar si hay fechas de vencimiento más próximas con stock disponible
      const earlierExpiryWithStock = sortedExpiry.filter(exp => {
        const expDate = new Date(exp.expiry_date || exp.value)
        const hasStock = exp.quantity > 0
        const isEarlier = expDate < scannedDate
        return isEarlier && hasStock
      })
      
      if (earlierExpiryWithStock.length > 0) {
        const earliestExpiry = earlierExpiryWithStock[0]
        return {
          compliant: false,
          warning: `FEFO: Hay ${earlierExpiryWithStock.length} fecha(s) de vencimiento más próxima(s) disponible(s). La más próxima vence el ${this.formatDate(earliestExpiry.expiry_date || earliestExpiry.value)} con ${earliestExpiry.quantity} unidades`,
          suggestedItem: {
            type: 'expiry_date',
            value: earliestExpiry.expiry_date || earliestExpiry.value,
            available_quantity: earliestExpiry.quantity
          }
        }
      }
      
      return { compliant: true, warning: null, suggestedItem: null }
    }
  }
}
</script>

<style scoped>
.egresos-container {
  padding: 20px;
}

.egresos-title {
  font-size: 1.5rem;
  font-weight: 500;
}

.egresos-filters .v-col {
  padding: 4px 8px;
}

.egresos-table {
  margin-top: 16px;
}

/* Estilos para la tabla de items */
.egresos-items-table {
  width: 100%;
  table-layout: fixed;
}

.egresos-items-table th,
.egresos-items-table td {
  text-align: left;
  vertical-align: middle;
  padding: 8px 12px !important;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.egresos-items-table .product-col {
  width: 25%;
  min-width: 200px;
}

.egresos-items-table .sku-col {
  width: 12%;
  min-width: 100px;
}

.egresos-items-table .quantity-col {
  width: 8%;
  min-width: 80px;
  text-align: center;
}

.egresos-items-table .lot-col {
  width: 12%;
  min-width: 100px;
}

.egresos-items-table .expiry-col {
  width: 10%;
  min-width: 100px;
}

.egresos-items-table .serial-col {
  width: 10%;
  min-width: 100px;
}

.egresos-items-table .production-col {
  width: 10%;
  min-width: 120px;
}

.egresos-items-table .strategy-col {
  width: 8%;
  min-width: 90px;
  text-align: center;
}

.egresos-items-table .actions-col {
  width: 5%;
  min-width: 60px;
  text-align: center;
}

/* Estilos para el modal de reporte */
.report-content {
  max-height: 70vh;
  overflow-y: auto;
}

.report-header h3 {
  color: #1976d2;
  margin-bottom: 15px;
}

.report-table {
  font-size: 0.9em;
}

.report-table th {
  background-color: #f5f5f5;
  font-weight: bold;
}

.report-summary {
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.report-summary > div {
  padding: 5px 0;
}

/* Estilos para gap entre botones */
.d-flex.gap-2 {
  gap: 8px;
}

/* Responsividad */
@media (max-width: 768px) {
  .egresos-container {
    padding: 10px;
  }
  
  .egresos-filters .v-col {
    padding: 2px 4px;
  }
  
  /* Botones responsivos en mobile */
  .d-flex.gap-2 {
    gap: 4px;
  }
}
</style> 